import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { GLOBAL_UTILS } from '../../styles/globalStyles';
import QuranRecordSection from './QuranRecordSection';
import { FiRefreshCw } from 'react-icons/fi';
import { ENV_CONFIG } from '../../config/environment';
import ApiServiceAdmin from '../../services/ApiServiceAdmin';
import { QuranRecord } from '../../types/api-routes';
import { 
  QuranSelection, 
  formatPosition, 
  formatQuranSelection
} from '../../utils/quranFormattingUtils';
import SearchableSelectOptions from './SearchableSelectOptions';
import { quranTrackingTranslations } from '../../locales';
import { useAssociation } from '../../contexts/AssociationContext';
import { todayInTz } from '../../utils/dateTime';

// Wrapper component pour l'icône de rafraîchissement (compatible TypeScript)
const RefreshIcon: React.FC<{ size?: number; style?: React.CSSProperties; animate?: boolean }> = ({ size = 16, style, animate = false }) => {
  const IconComponent = FiRefreshCw as any;
  return <IconComponent size={size} style={{ ...style, animation: animate ? 'spin 1s linear infinite' : 'none' }} />;
};

// Interfaces
interface Student {
  id: number;
  student_name: string;
  level?: string;
  guardian_name?: string;
  phone?: string;
  average?: number;
  attendance?: number;
  status?: string;
  class_id?: number;
  class_name?: string;
  studentName?: string;
  hizbCount?: number;
  averageGrade?: number;
  gender?: 'Female' | 'Male';
}


// QuranSelection est maintenant importé depuis quranFormattingUtils

interface QuranTrackingSectionProps {
  // Données
  students: Student[];
  quranRecords: QuranRecord[];
  
  // Fonctions API
  onRefreshQuranRecords: () => Promise<boolean>;
  onRefreshStats?: () => Promise<void>;
  onGetStudents: () => Promise<{ success: boolean; data?: any[] }>;
  onGetQuranRecordById?: (id: number) => Promise<{ success: boolean; data?: any; error?: string }>;
  onAddQuranRecord: (record: any) => Promise<{ success: boolean; error?: string }>;
  onUpdateQuranRecord: (id: number, record: any) => Promise<{ success: boolean; error?: string }>;
  onUpdateRepeatNewHifz?: (recordId: number, repeatNewHifz: number) => Promise<{ success: boolean; error?: string }>;
  
  // Traductions
  t: (key: string) => string;
  currentLanguage?: string;
  windowWidth?: number;
  
  // Styles
  styles?: any;
  
  // Callbacks
  onRecordAdded?: () => void;
  onRecordUpdated?: () => void;
  
  // Options d'affichage
  hideTitle?: boolean; // Masquer le titre si déjà affiché par le parent
  hideHeader?: boolean; // Masquer tout le header (titre, filtres, boutons) si déjà affiché par le parent
  selectedStudentFilter?: string; // Filtre étudiant contrôlé depuis le parent
  onStudentFilterChange?: (value: string) => void; // Callback pour changer le filtre étudiant
  
  // Filtre professeur (pour AdminDashboard)
  teachers?: Array<{ id: number; full_name?: string; username?: string; status?: string }>; // Liste des professeurs
  selectedTeacherFilter?: string; // Filtre professeur contrôlé depuis le parent
  onTeacherFilterChange?: (value: string) => void; // Callback pour changer le filtre professeur
  
  // Nom du professeur connecté (pour TeacherDashboard)
  currentTeacherName?: string; // Nom du professeur connecté (pour affichage dans la modale)
  
  // Permissions
  canModify?: boolean; // Si false, masque les boutons d'ajout, modification et suppression
  
  // Callback pour mettre à jour repeatNewHifz dans quran_next_session (uniquement pour les parents)
  onUpdateRepeatNewHifzInNextSession?: (studentId: number, newValue: number) => Promise<void>;
  
  // Callbacks pour gérer quran_next_session séparément
  onCreateOrUpdateNextSession?: (studentId: number, sessionData: any) => Promise<{ success: boolean; error?: string }>;
  onGetNextSessionForStudent?: (studentId: number) => Promise<{ success: boolean; data?: any; error?: string }>;
  
  // Callback pour récupérer les IDs des étudiants d'un professeur (uniquement pour AdminDashboard)
  onGetStudentIdsByTeacher?: (teacherId: number) => Promise<{ success: boolean; data?: number[]; error?: string }>;
}

// Caractères spéciaux invisibles pour délimiter le texte d'évaluation
const EVAL_START_MARKER = '\u200C\u200D';
const EVAL_END_MARKER = '\u200D\u200C';

const QuranTrackingSection: React.FC<QuranTrackingSectionProps> = ({
  students,
  quranRecords,
  onRefreshQuranRecords,
  onRefreshStats,
  onGetStudents,
  onGetQuranRecordById,
  onAddQuranRecord,
  onUpdateQuranRecord,
  t,
  currentLanguage = 'ar',
  windowWidth = 1024,
  styles,
  onRecordAdded,
  onRecordUpdated,
  hideTitle = false,
  hideHeader = false,
  selectedStudentFilter: externalStudentFilter,
  onStudentFilterChange,
  teachers,
  selectedTeacherFilter: externalTeacherFilter,
  onTeacherFilterChange,
  currentTeacherName,
  canModify = true,
  onUpdateRepeatNewHifz,
  onUpdateRepeatNewHifzInNextSession,
  onCreateOrUpdateNextSession,
  onGetNextSessionForStudent,
  onGetStudentIdsByTeacher
}) => {
  // États locaux
  // ✅ Fuseau horaire de l'ASSOCIATION (paramètre `default_timezone`) — utilisé pour « aujourd'hui »
  const { timezone } = useAssociation();

  const [internalStudentFilter, setInternalStudentFilter] = useState<string>('all');
  const [internalTeacherFilter, setInternalTeacherFilter] = useState<string>('all');
  const [studentSearchText, setStudentSearchText] = useState<string>('');
  const [showStudentDropdown, setShowStudentDropdown] = useState<boolean>(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [sortBy, setSortBy] = useState<'createdDate' | 'alphabetical'>('createdDate'); // État pour le tri (setSortBy sera utilisé plus tard)
  
  // État par élève pour contrôler l'affichage des sessions précédentes (accordion)
  const [expandedStudentIds, setExpandedStudentIds] = useState<Set<number>>(new Set());
  
  // Fonction pour toggle les sessions précédentes d'un élève (accordion — un seul ouvert à la fois)
  const toggleStudentSessions = (studentId: number) => {
    setExpandedStudentIds(prev => {
      const next = new Set(prev);
      if (next.has(studentId)) {
        next.delete(studentId);
      } else {
        next.clear(); // accordion : ferme les autres
        next.add(studentId);
      }
      return next;
    });
  };
  
  // Détection mobile
  const isMobile = windowWidth < 768;
  const isRTL = currentLanguage === 'ar';
  const qt = quranTrackingTranslations[(currentLanguage as 'ar' | 'fr' | 'en') || 'ar'];

  // Helper: convertit une valeur d'évaluation (anglais ou arabe) en texte traduit
  const mapEvalToDisplay = (evalValue: string | undefined | null): string => {
    if (!evalValue || evalValue === 'NotDone') return qt.evalNotDone;
    const map: Record<string, string> = {
      'Excellent': qt.evalExcellent, 'ممتاز': qt.evalExcellent,
      'Good': qt.evalGood,           'جيد': qt.evalGood,
      'Moyen': qt.evalMoyen,         'متوسط': qt.evalMoyen,
      'Repeat': qt.evalRepeat,       'إعادة': qt.evalRepeat,
      'Absent': qt.evalAbsent,       'غائب': qt.evalAbsent,
      'NotDone': qt.evalNotDone,     'لم يتم': qt.evalNotDone,
    };
    return map[evalValue] || evalValue;
  };
  
  // Utiliser le filtre externe si fourni, sinon utiliser l'état local
  const currentStudentFilter = externalStudentFilter !== undefined ? externalStudentFilter : internalStudentFilter;
  const currentTeacherFilter = externalTeacherFilter !== undefined ? externalTeacherFilter : internalTeacherFilter;
  
  // Rafraîchissement automatique au montage du composant et périodique
  React.useEffect(() => {
    // Rafraîchir les données automatiquement au chargement initial
    const initialRefresh = async () => {
      if (onRefreshQuranRecords) {
        await onRefreshQuranRecords();
      }
    };
    initialRefresh();

    // Rafraîchissement automatique périodique (toutes les 30 secondes)
    const intervalId = setInterval(async () => {
      if (onRefreshQuranRecords && !refreshing) {
        await onRefreshQuranRecords();
      }
    }, 30000); // 30 secondes

    // Rafraîchissement quand la fenêtre redevient visible (retour sur la page)
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible' && onRefreshQuranRecords && !refreshing) {
        await onRefreshQuranRecords();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Nettoyage
    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Seulement au montage

  // Mettre à jour studentSearchText quand currentStudentFilter change depuis l'extérieur
  React.useEffect(() => {
    if (currentStudentFilter === 'all') {
      if (studentSearchText && !showStudentDropdown) {
        setStudentSearchText('');
      }
    } else {
      const selectedStudent = students.find(s => s.id.toString() === currentStudentFilter);
      if (selectedStudent && !showStudentDropdown) {
        const studentName = selectedStudent.studentName || selectedStudent.student_name || '';
        if (studentSearchText !== studentName) {
          setStudentSearchText(studentName);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStudentFilter, students]);

  // Utiliser des refs pour capturer les tableaux de manière stable
  const studentsRef = React.useRef(students);
  const quranRecordsRef = React.useRef(quranRecords);
  const tRef = React.useRef(t);
  
  // Mémoriser les IDs des étudiants et des enregistrements pour éviter les recalculs inutiles
  const studentsIds = useMemo(() => {
    const ids = students.map(s => s.id).join(',');
    studentsRef.current = students;
    return ids;
  }, [students]);
  // Mettre à jour la ref immédiatement quand quranRecords change
  React.useEffect(() => {
    quranRecordsRef.current = quranRecords;
    // Nettoyer le cache quand les enregistrements changent
    latestRecordCacheRef.current.clear();
    // ⚠️ Ne PAS nettoyer nextSessionCacheRef ici car cela efface "الجلسة القادمة" lors de l'ouverture du modal
    // Le cache de nextSessionCacheRef ne doit être nettoyé que lors d'un refresh explicite (handleRefresh) ou après une modification réussie
    // nextSessionCacheRef.current.clear(); // ❌ DÉSACTIVÉ - Ne pas nettoyer lors de l'ouverture du modal
    // setNextSessionDataLoaded(new Map()); // ❌ DÉSACTIVÉ - Ne pas réinitialiser lors de l'ouverture du modal
  }, [quranRecords]);
  
  // Utiliser une comparaison basée uniquement sur les IDs pour éviter les recalculs inutiles
  // Créer une string d'IDs triés pour une comparaison stable
  // Utiliser une ref pour comparer les IDs précédents et éviter les recalculs inutiles
  const prevQuranRecordsIdsRef = React.useRef<string>('');
  const quranRecordsIds = useMemo(() => {
    const ids = quranRecords.map(r => r.id).sort((a, b) => a - b).join(',');
    // Ne recalculer que si les IDs ont vraiment changé
    if (ids !== prevQuranRecordsIdsRef.current) {
      prevQuranRecordsIdsRef.current = ids;
    }
    return prevQuranRecordsIdsRef.current;
  }, [quranRecords]);
  
  // Mettre à jour les refs pour t
  React.useEffect(() => {
    tRef.current = t;
  }, [t]);
  
  const handleStudentFilterChange = (value: string) => {
    if (onStudentFilterChange) {
      onStudentFilterChange(value);
    } else {
      setInternalStudentFilter(value);
    }
  };
  
  const handleTeacherFilterChange = (value: string) => {
    if (onTeacherFilterChange) {
      onTeacherFilterChange(value);
    } else {
      setInternalTeacherFilter(value);
    }
  };
  const [showQuranRecordModal, setShowQuranRecordModal] = useState(false);
  const [activeTab, setActiveTab] = useState<'lastSession' | 'nextSession'>('lastSession');
  const [modalStudents, setModalStudents] = useState<Student[]>([]);
  const [loadingModalStudents, setLoadingModalStudents] = useState(false);
  const [editingRecordId, setEditingRecordId] = useState<number | null>(null);
  const [newQuranRecord, setNewQuranRecord] = useState({
    studentId: '',
    totalHizb: '',
    // آخر جلسة
    newHifzTxt: '',
    oldHifzMurajahTxt: '',
    newHifzMurajahText: '',  // ✅ NOUVEAU
    note: '',
    // الجلسة القادمة
    nextHalaqaNewHifzText: '',
    nextHalaqaOldHifzMurajahText: '',  // ✅ NOUVEAU
    nextHalaqaNewHifzMurajahText: '',  // ✅ NOUVEAU
    startPoint: 'الناس'
  });
  const [calculatingHizb, setCalculatingHizb] = useState(false);
  const [newMemorizationEvaluation, setNewMemorizationEvaluation] = useState('لم يتم'); // ✅ Défaut: NotDone
  const [reviewEvaluation, setReviewEvaluation] = useState('لم يتم'); // ✅ Défaut: NotDone
  const [newHifzMurajahEvaluation, setNewHifzMurajahEvaluation] = useState('لم يتم'); // ✅ NOUVEAU - pour مراجعة الحفظ القريب (défaut: NotDone)
  // Nouveaux champs d'évaluation pour l'API (en anglais)
  const [evalNewHifz, setEvalNewHifz] = useState<'Excellent' | 'Good' | 'Moyen' | 'Repeat' | 'Absent' | 'NotDone'>('NotDone'); // تقييم الحفظ الجديد
  const [evalOldHifzMurajah, setEvalOldHifzMurajah] = useState<'Excellent' | 'Good' | 'Moyen' | 'Repeat' | 'Absent' | 'NotDone'>('NotDone'); // تقييم مراجعة الحفظ القديم
  const [evalNewHifzMurajah, setEvalNewHifzMurajah] = useState<'Excellent' | 'Good' | 'Moyen' | 'Repeat' | 'Absent' | 'NotDone'>('NotDone'); // تقييم مراجعة الحفظ القريب
  const [repeatNewHifz, setRepeatNewHifz] = useState<number>(0); // ✅ NOUVEAU - Valeur de "تكرار الحفظ الجديد" de "الجلسة القادمة"
  const [targetRepeatNewHifzCount, setTargetRepeatNewHifzCount] = useState<number>(15); // ✅ Paramètre depuis l'API (défaut: 15)
  const [maxPreviousSessions, setMaxPreviousSessions] = useState<number>(10); // ✅ Paramètre depuis l'API : nb max de sessions précédentes affichées (0 = illimité)
  const [refreshing, setRefreshing] = useState<string | null>(null);
  
  // Fonction de mapping entre valeurs arabes et anglaises
  const mapArabicToEnglishEval = useCallback((arabicValue: string): 'Excellent' | 'Good' | 'Moyen' | 'Repeat' | 'Absent' | 'NotDone' => {
    if (arabicValue === 'ممتاز') return 'Excellent';
    if (arabicValue === 'جيد') return 'Good';
    if (arabicValue === 'متوسط') return 'Moyen';
    if (arabicValue === 'إعادة' || arabicValue === 'إعادة الحفظ' || arabicValue === 'إعادة المراجعة') return 'Repeat'; // Garde anciens formats pour rétrocompatibilité
    if (arabicValue === 'غائب') return 'Absent';
    if (arabicValue === 'لم يتم' || arabicValue === '' || !arabicValue) return 'NotDone';
    return 'Moyen'; // Valeur par défaut
  }, []);
  
  // Fonction de mapping entre valeurs anglaises et arabes (pour l'affichage - sans préfixe)
  const mapEnglishToArabicEval = useCallback((englishValue: string, type?: 'newMemorization' | 'review'): string => {
    if (englishValue === 'Excellent') return 'ممتاز';
    if (englishValue === 'Good') return 'جيد';
    if (englishValue === 'Moyen') return 'متوسط';
    if (englishValue === 'Repeat') return 'إعادة';
    if (englishValue === 'Absent') return 'غائب';
    if (englishValue === 'NotDone') return 'لم يتم';
    return 'متوسط'; // Valeur par défaut
  }, []);
  
  // Wrappers pour synchroniser les évaluations arabes avec les valeurs anglaises pour l'API
  // ✅ NOUVEAU : Intégration de la logique "Absent"
  const handleNewMemorizationEvaluationChange = useCallback((arabicValue: React.SetStateAction<string>) => {
    const value = typeof arabicValue === 'function' ? arabicValue(newMemorizationEvaluation) : arabicValue;
    const oldValue = newMemorizationEvaluation;
    
    // Règle 1 : Si on passe à "غائب" (Absent), propager aux autres
    if (value === 'غائب') {
      setNewMemorizationEvaluation('غائب');
      setReviewEvaluation('غائب');
      setNewHifzMurajahEvaluation('غائب');
      setEvalNewHifz('Absent');
      setEvalOldHifzMurajah('Absent');
      setEvalNewHifzMurajah('Absent');
    }
    // Règle 2 : Si on sort de "غائب", les autres "غائب" passent à "لم يتم"
    else if (oldValue === 'غائب') {
      setNewMemorizationEvaluation(value);
      setEvalNewHifz(mapArabicToEnglishEval(value));
      
      if (reviewEvaluation === 'غائب') {
        setReviewEvaluation('لم يتم');
        setEvalOldHifzMurajah('NotDone');
      }
      if (newHifzMurajahEvaluation === 'غائب') {
        setNewHifzMurajahEvaluation('لم يتم');
        setEvalNewHifzMurajah('NotDone');
      }
    }
    // Cas normal : changement simple
    else {
      setNewMemorizationEvaluation(value);
      const englishValue = mapArabicToEnglishEval(value);
      setEvalNewHifz(englishValue);
    }
  }, [mapArabicToEnglishEval, newMemorizationEvaluation, reviewEvaluation, newHifzMurajahEvaluation]);
  
  const handleReviewEvaluationChange = useCallback((arabicValue: React.SetStateAction<string>) => {
    const value = typeof arabicValue === 'function' ? arabicValue(reviewEvaluation) : arabicValue;
    const oldValue = reviewEvaluation;
    
    // Règle 1 : Si on passe à "غائب" (Absent), propager aux autres
    if (value === 'غائب') {
      setNewMemorizationEvaluation('غائب');
      setReviewEvaluation('غائب');
      setNewHifzMurajahEvaluation('غائب');
      setEvalNewHifz('Absent');
      setEvalOldHifzMurajah('Absent');
      setEvalNewHifzMurajah('Absent');
    }
    // Règle 2 : Si on sort de "غائب", les autres "غائب" passent à "لم يتم"
    else if (oldValue === 'غائب') {
      setReviewEvaluation(value);
      setEvalOldHifzMurajah(mapArabicToEnglishEval(value));
      
      if (newMemorizationEvaluation === 'غائب') {
        setNewMemorizationEvaluation('لم يتم');
        setEvalNewHifz('NotDone');
      }
      if (newHifzMurajahEvaluation === 'غائب') {
        setNewHifzMurajahEvaluation('لم يتم');
        setEvalNewHifzMurajah('NotDone');
      }
    }
    // Cas normal : changement simple
    else {
      setReviewEvaluation(value);
      const englishValue = mapArabicToEnglishEval(value);
      setEvalOldHifzMurajah(englishValue);
    }
  }, [mapArabicToEnglishEval, reviewEvaluation, newMemorizationEvaluation, newHifzMurajahEvaluation]);
  
  const handleNewHifzMurajahEvaluationChange = useCallback((arabicValue: React.SetStateAction<string>) => {
    const value = typeof arabicValue === 'function' ? arabicValue(newHifzMurajahEvaluation) : arabicValue;
    const oldValue = newHifzMurajahEvaluation;
    
    // Règle 1 : Si on passe à "غائب" (Absent), propager aux autres
    if (value === 'غائب') {
      setNewMemorizationEvaluation('غائب');
      setReviewEvaluation('غائب');
      setNewHifzMurajahEvaluation('غائب');
      setEvalNewHifz('Absent');
      setEvalOldHifzMurajah('Absent');
      setEvalNewHifzMurajah('Absent');
    }
    // Règle 2 : Si on sort de "غائب", les autres "غائب" passent à "لم يتم"
    else if (oldValue === 'غائب') {
      setNewHifzMurajahEvaluation(value);
      setEvalNewHifzMurajah(mapArabicToEnglishEval(value));
      
      if (newMemorizationEvaluation === 'غائب') {
        setNewMemorizationEvaluation('لم يتم');
        setEvalNewHifz('NotDone');
      }
      if (reviewEvaluation === 'غائب') {
        setReviewEvaluation('لم يتم');
        setEvalOldHifzMurajah('NotDone');
      }
    }
    // Cas normal : changement simple
    else {
      setNewHifzMurajahEvaluation(value);
      const englishValue = mapArabicToEnglishEval(value);
      setEvalNewHifzMurajah(englishValue);
    }
  }, [mapArabicToEnglishEval, newHifzMurajahEvaluation, newMemorizationEvaluation, reviewEvaluation]);
  
  // États pour les sourates et versets
  const [surahs, setSurahs] = useState<Array<{id: number; pos: number; ar: string}>>([]);
  const [loadingSurahs, setLoadingSurahs] = useState(false);
  const [surahsError, setSurahsError] = useState<string | null>(null);
  const [verseCounts, setVerseCounts] = useState<{[key: number]: number}>({});
  
  const [newMemorizationSelection, setNewMemorizationSelection] = useState<QuranSelection>({
    startSurah: 0,
    startVerse: 0,
    endSurah: 0,
    endVerse: 0
  });
  const [reviewSelection, setReviewSelection] = useState<QuranSelection>({
    startSurah: 0,
    startVerse: 0,
    endSurah: 0,
    endVerse: 0
  });
  const [newHifzMurajahSelection, setNewHifzMurajahSelection] = useState<QuranSelection>({
    startSurah: 0,
    startVerse: 0,
    endSurah: 0,
    endVerse: 0
  });
  // الجلسة القادمة
  const [assignmentSelection, setAssignmentSelection] = useState<QuranSelection>({
    startSurah: 0,
    startVerse: 0,
    endSurah: 0,
    endVerse: 0
  });
  const [nextHalaqaOldMurajahSelection, setNextHalaqaOldMurajahSelection] = useState<QuranSelection>({
    startSurah: 0,
    startVerse: 0,
    endSurah: 0,
    endVerse: 0
  });
  const [nextHalaqaNewMurajahSelection, setNextHalaqaNewMurajahSelection] = useState<QuranSelection>({
    startSurah: 0,
    startVerse: 0,
    endSurah: 0,
    endVerse: 0
  });

  // Fonction pour extraire le texte original sans le texte d'évaluation
  const extractOriginalNoteText = useCallback((note: string): string => {
    if (!note) return '';
    
    let cleanedNote = note;
    
    // 1. Supprimer le texte d'évaluation délimité par les marqueurs invisibles
    const evalPattern = new RegExp(
      `\\s*•\\s*${EVAL_START_MARKER.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}[^${EVAL_END_MARKER.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}]*${EVAL_END_MARKER.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`,
      'g'
    );
    cleanedNote = cleanedNote.replace(evalPattern, '').trim();
    
    // 2. Supprimer aussi les marqueurs invisibles seuls
    cleanedNote = cleanedNote.replace(new RegExp(`${EVAL_START_MARKER.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}|${EVAL_END_MARKER.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`, 'g'), '');
    
    // 3. Supprimer les patterns de texte d'évaluation même sans marqueurs
    let previousNote = '';
    while (previousNote !== cleanedNote) {
      previousNote = cleanedNote;
      const evaluationPatterns = [
        /\s*•\s*(ممتاز|جيد|متوسط|إعادة|غائب|لم يتم|بدون تقييم)(\s*•\s*(ممتاز|جيد|متوسط|إعادة|غائب|لم يتم|بدون تقييم))*$/,
        /^(ممتاز|جيد|متوسط|إعادة|غائب|لم يتم|بدون تقييم)(\s*•\s*(ممتاز|جيد|متوسط|إعادة|غائب|لم يتم|بدون تقييم))*$/,
        /\s*•\s*(ممتاز|جيد|متوسط|إعادة|غائب|لم يتم|بدون تقييم)\s*•/g,
        /\s*•\s*(ممتاز|جيد|متوسط|إعادة|غائب|لم يتم|بدون تقييم)$/g,
        /^(ممتاز|جيد|متوسط|إعادة|غائب|لم يتم|بدون تقييم)\s*•/g
      ];
      
      for (const pattern of evaluationPatterns) {
        cleanedNote = cleanedNote.replace(pattern, '').trim();
      }
    }
    
    // 4. Nettoyer aussi les séparateurs " • " orphelins et multiples
    cleanedNote = cleanedNote.replace(/\s*•\s*•\s*/g, ' • ');
    cleanedNote = cleanedNote.replace(/\s*•\s*$/, '').trim();
    cleanedNote = cleanedNote.replace(/^\s*•\s*/, '').trim();
    
    return cleanedNote;
  }, []);

  // Fonction pour envelopper le texte d'évaluation avec les marqueurs invisibles
  const wrapEvaluationText = useCallback((evaluationText: string): string => {
    if (!evaluationText) return '';
    return `${EVAL_START_MARKER}${evaluationText}${EVAL_END_MARKER}`;
  }, []);

  // Fonction pour charger les sourates depuis la base de données
  const loadSurahs = useCallback(async () => {
    if (surahs.length > 0) return; // Déjà chargées
    
    setLoadingSurahs(true);
    setSurahsError(null);
    try {
      const baseUrl = ENV_CONFIG.apiBaseUrl;
      const response = await fetch(`${baseUrl}/api/quran/surahs`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Gérer différents formats de réponse
      let surahsData: any[] = [];
      if (data.success && data.data && Array.isArray(data.data)) {
        surahsData = data.data;
      } else if (Array.isArray(data.data)) {
        surahsData = data.data;
      } else if (Array.isArray(data)) {
        surahsData = data;
      } else {
        throw new Error('Format de réponse invalide');
      }
      
      if (surahsData.length === 0) {
        setSurahsError('لا توجد بيانات سور متاحة');
        return;
      }
      
      // Mapper les sourates depuis la base de données
      const mappedSurahs = surahsData.map((surah: any) => ({
        id: surah.id || surah.pos || surah.number,
        pos: surah.pos || surah.number || surah.id,
        ar: surah.name_ar || surah.name || surah.ar || 'غير محدد'
      }));
      
      setSurahs(mappedSurahs);
    } catch (error) {
      console.error('Erreur lors du chargement des sourates:', error);
      setSurahsError('خطأ في تحميل السور من قاعدة البيانات');
    } finally {
      setLoadingSurahs(false);
    }
  }, [surahs.length]);

  // Utiliser useRef pour capturer les fonctions de manière stable
  const onGetStudentsRef = React.useRef(onGetStudents);
  const loadSurahsRef = React.useRef(loadSurahs);
  const extractOriginalNoteTextRef = React.useRef(extractOriginalNoteText);
  const surahsLengthRef = React.useRef(surahs.length);

  // Mettre à jour les refs quand les valeurs changent
  React.useEffect(() => {
    onGetStudentsRef.current = onGetStudents;
    loadSurahsRef.current = loadSurahs;
    extractOriginalNoteTextRef.current = extractOriginalNoteText;
    surahsLengthRef.current = surahs.length;
  }, [onGetStudents, loadSurahs, extractOriginalNoteText, surahs.length]);

  // Fonction pour charger le nombre de versets d'une sourate
  const loadVerseCount = useCallback(async (surahPos: number) => {
    if (verseCounts[surahPos]) return; // Déjà chargé
    
    try {
      const response = await fetch(`https://api.alquran.cloud/v1/surah/${surahPos}`);
      if (response.ok) {
        const data = await response.json();
        if (data.data && data.data.numberOfAyahs) {
          setVerseCounts(prev => ({ ...prev, [surahPos]: data.data.numberOfAyahs }));
        }
      }
    } catch (error) {
      console.error(`Erreur lors du chargement du nombre de versets pour la sourate ${surahPos}:`, error);
    }
  }, [verseCounts]);

  // ✅ Fonction commune pour charger les données d'un enregistrement dans le formulaire
  const loadRecordDataIntoForm = useCallback(async (record: any) => {
    if (!record) return;
    
    console.log('🔍 loadRecordDataIntoForm - Chargement des données:', record);
    
    // Parser une position
    const parsePosition = (pos: string | QuranSelection | undefined): QuranSelection => {
      if (!pos) return { startSurah: 0, startVerse: 0, endSurah: 0, endVerse: 0 };
      if (typeof pos === 'object' && 'startSurah' in pos) {
        return pos as QuranSelection;
      }
      if (typeof pos === 'string' && pos.includes('-')) {
        const startEndPattern = /^(\d+):(\d+)-(\d+):(\d+)$/;
        const match = pos.match(startEndPattern);
        if (match) {
          return {
            startSurah: parseInt(match[1]),
            startVerse: parseInt(match[2]),
            endSurah: parseInt(match[3]),
            endVerse: parseInt(match[4])
          };
        }
      }
      return { startSurah: 0, startVerse: 0, endSurah: 0, endVerse: 0 };
    };
    
    // ✅ Récupérer les données de quran_next_session depuis le cache ou l'API
    const studentId = record.studentId || record.student_id;
    let nextSessionData: {
      nextHalaqaNewHifzText?: string;
      nextHalaqaOldHifzMurajahText?: string;
      nextHalaqaNewHifzMurajahText?: string;
      nextHalaqaNewHifzRange?: string | null;
      nextHalaqaOldHifzMurajahRange?: string | null;
      nextHalaqaNewHifzMurajahRange?: string | null;
      repeatNewHifz?: number;
      [key: string]: any;
    } | null = null;
    if (studentId && onGetNextSessionForStudent) {
      // Vérifier d'abord le cache
      if (nextSessionCacheRef.current.has(studentId)) {
        nextSessionData = nextSessionCacheRef.current.get(studentId);
      } else {
        // Récupérer depuis l'API
        try {
          const response = await onGetNextSessionForStudent(studentId);
          if (response.success && response.data) {
            nextSessionData = response.data;
            // Stocker dans le cache
            nextSessionCacheRef.current.set(studentId, response.data);
          }
        } catch (error) {
          console.warn(`⚠️ Erreur lors de la récupération de quran_next_session pour studentId ${studentId}:`, error);
        }
      }
    }
    
    // 1. Charger les données texte dans newQuranRecord
    setNewQuranRecord(prev => ({
      ...prev,
      studentId: String(record.studentId || record.student_id || prev.studentId),
      totalHizb: (record.totalHizb != null) ? String(record.totalHizb) : 
                 (record.total_hizb != null) ? String(record.total_hizb) : '',
      // ✅ آخر جلسة (Last Session) - depuis quran_records
      newHifzTxt: record.newHifzTxt || '',
      oldHifzMurajahTxt: record.oldHifzMurajahTxt || '',
      newHifzMurajahText: record.newHifzMurajahText || '',
      // ✅ الجلسة القادمة (Next Session) - depuis quran_next_session
      nextHalaqaNewHifzText: nextSessionData ? (nextSessionData.nextHalaqaNewHifzText || '') : '',
      nextHalaqaOldHifzMurajahText: nextSessionData ? (nextSessionData.nextHalaqaOldHifzMurajahText || '') : '',
      nextHalaqaNewHifzMurajahText: nextSessionData ? (nextSessionData.nextHalaqaNewHifzMurajahText || '') : '',
      note: extractOriginalNoteText(record.note || ''),
      startPoint: record.startPoint || record.start_point || 'الناس'
    }));
    
    // 2. Charger les évaluations
    setEvalNewHifz(record.evalNewHifz as any || 'NotDone');
    setEvalOldHifzMurajah(record.evalOldHifzMurajah as any || 'NotDone');
    setEvalNewHifzMurajah(record.evalNewHifzMurajah as any || 'NotDone');
    setNewMemorizationEvaluation(mapEnglishToArabicEval(record.evalNewHifz || 'NotDone', 'newMemorization'));
    setReviewEvaluation(mapEnglishToArabicEval(record.evalOldHifzMurajah || 'NotDone', 'review'));
    setNewHifzMurajahEvaluation(mapEnglishToArabicEval(record.evalNewHifzMurajah || 'NotDone', 'review'));
    
    // 3. Charger les positions (ranges) - آخر جلسة
    setNewMemorizationSelection(parsePosition(record.newHifzRange));
    setReviewSelection(parsePosition(record.oldHifzMurajahRange));
    setNewHifzMurajahSelection(parsePosition(record.newHifzMurajahRange));
    
    // 4. Charger les positions (ranges) - الجلسة القادمة - depuis quran_next_session
    setAssignmentSelection(parsePosition(nextSessionData ? (nextSessionData.nextHalaqaNewHifzRange || undefined) : undefined));
    setNextHalaqaOldMurajahSelection(parsePosition(nextSessionData ? (nextSessionData.nextHalaqaOldHifzMurajahRange || undefined) : undefined));
    setNextHalaqaNewMurajahSelection(parsePosition(nextSessionData ? (nextSessionData.nextHalaqaNewHifzMurajahRange || undefined) : undefined));
    
    console.log('✅ loadRecordDataIntoForm - Données chargées avec succès');
  }, [extractOriginalNoteText, mapEnglishToArabicEval, onGetNextSessionForStudent]);

  // Fonction pour fermer le modal d'ajout
  const closeAddQuranRecordModal = useCallback(() => {
    setShowQuranRecordModal(false);
    setEditingRecordId(null);
    setNewQuranRecord({
      studentId: '',
      totalHizb: '',
      newHifzTxt: '',
      oldHifzMurajahTxt: '',
      newHifzMurajahText: '',
      nextHalaqaNewHifzText: '',
      nextHalaqaOldHifzMurajahText: '',
      nextHalaqaNewHifzMurajahText: '',
      note: '',
      startPoint: 'الناس'
    });
    setNewMemorizationSelection({
      startSurah: 0,
      startVerse: 0,
      endSurah: 0,
      endVerse: 0
    });
    setReviewSelection({
      startSurah: 0,
      startVerse: 0,
      endSurah: 0,
      endVerse: 0
    });
    setNewHifzMurajahSelection({
      startSurah: 0,
      startVerse: 0,
      endSurah: 0,
      endVerse: 0
    });
    setAssignmentSelection({
      startSurah: 0,
      startVerse: 0,
      endSurah: 0,
      endVerse: 0
    });
    setNextHalaqaOldMurajahSelection({
      startSurah: 0,
      startVerse: 0,
      endSurah: 0,
      endVerse: 0
    });
    setNextHalaqaNewMurajahSelection({
      startSurah: 0,
      startVerse: 0,
      endSurah: 0,
      endVerse: 0
    });
    setNewMemorizationEvaluation('متوسط');
    setReviewEvaluation('متوسط');
    setEvalNewHifz('Moyen');
    setEvalOldHifzMurajah('Moyen');
  }, []);

  // ✅ Fonction réutilisable pour ouvrir le modal d'ajout/édition (logique simple)
  const openQuranRecordModal = useCallback(async (recordToEdit?: any) => {
    // ✅ NOUVEAU : Vérifier si un professeur est sélectionné (uniquement pour AdminDashboard, et seulement en mode ajout)
    if (teachers && teachers.length > 0 && !recordToEdit) {
      // On est dans AdminDashboard (teachers est fourni)
      if (currentTeacherFilter === 'all' || !currentTeacherFilter) {
        alert('يجب اختيار أستاذ قبل إضافة سجل جديد');
        return;
      }
    }
    
    // Réinitialiser toutes les sélections
    setNewMemorizationSelection({ startSurah: 0, startVerse: 0, endSurah: 0, endVerse: 0 });
    setReviewSelection({ startSurah: 0, startVerse: 0, endSurah: 0, endVerse: 0 });
    setNewHifzMurajahSelection({ startSurah: 0, startVerse: 0, endSurah: 0, endVerse: 0 });
    setAssignmentSelection({ startSurah: 0, startVerse: 0, endSurah: 0, endVerse: 0 });
    setNextHalaqaOldMurajahSelection({ startSurah: 0, startVerse: 0, endSurah: 0, endVerse: 0 });
    setNextHalaqaNewMurajahSelection({ startSurah: 0, startVerse: 0, endSurah: 0, endVerse: 0 });
    // ✅ Réinitialiser repeatNewHifz
    setRepeatNewHifz(0);

    // Si on édite un enregistrement existant
    if (recordToEdit) {
      console.log('🔍 openQuranRecordModal - Mode édition:', recordToEdit);
      console.log('🔍 openQuranRecordModal - Champs de position disponibles:', {
        newHifzRange: recordToEdit.newHifzRange,
        new_memorization_pos: recordToEdit.new_memorization_pos,
        newMemorizationPos: recordToEdit.newMemorizationPos,
        oldHifzMurajahRange: recordToEdit.oldHifzMurajahRange,
        review_pos: recordToEdit.review_pos,
        reviewPos: recordToEdit.reviewPos,
        newHifzMurajahRange: recordToEdit.newHifzMurajahRange,
        new_hifz_murajah_pos: recordToEdit.new_hifz_murajah_pos,
        nextHalaqaNewHifzRange: recordToEdit.nextHalaqaNewHifzRange,
        assignment_pos: recordToEdit.assignment_pos,
        assignmentPos: recordToEdit.assignmentPos
      });
      
      // Définir l'ID d'édition
      setEditingRecordId(recordToEdit.id);
      
      // Parser les positions depuis les champs du record
      const parsePosition = (pos: string | QuranSelection | undefined): QuranSelection => {
        if (!pos) return { startSurah: 0, startVerse: 0, endSurah: 0, endVerse: 0 };
        if (typeof pos === 'object' && 'startSurah' in pos) {
          return pos as QuranSelection;
        }
        if (typeof pos === 'string' && pos.includes('-')) {
          const startEndPattern = /^(\d+):(\d+)-(\d+):(\d+)$/;
          const match = pos.match(startEndPattern);
          if (match) {
            return {
              startSurah: parseInt(match[1]),
              startVerse: parseInt(match[2]),
              endSurah: parseInt(match[3]),
              endVerse: parseInt(match[4])
            };
          }
        }
        return { startSurah: 0, startVerse: 0, endSurah: 0, endVerse: 0 };
      };
      
      // Préparer l'objet avec toutes les données
      const recordData = {
        studentId: String(recordToEdit.studentId || recordToEdit.student_id || ''),
        totalHizb: recordToEdit.totalHizb ? String(recordToEdit.totalHizb) : 
                   (recordToEdit.total_hizb ? String(recordToEdit.total_hizb) : ''),
        // آخر جلسة
        newHifzTxt: recordToEdit.newHifzTxt || recordToEdit.new_memorization || recordToEdit.newMemorization || '',
        oldHifzMurajahTxt: recordToEdit.oldHifzMurajahTxt || recordToEdit.review || '',
        newHifzMurajahText: recordToEdit.newHifzMurajahText || '',
        // الجلسة القادمة
        nextHalaqaNewHifzText: recordToEdit.nextHalaqaNewHifzText || recordToEdit.assignment || '',
        nextHalaqaOldHifzMurajahText: recordToEdit.nextHalaqaOldHifzMurajahText || '',
        nextHalaqaNewHifzMurajahText: recordToEdit.nextHalaqaNewHifzMurajahText || '',
        note: extractOriginalNoteText(recordToEdit.note || ''),
        startPoint: recordToEdit.startPoint || recordToEdit.start_point || 'الناس',
        // Positions (ranges)
        // Chercher d'abord les champs camelCase (utilisés dans le code), puis les snake_case (backend)
        newHifzRange: parsePosition(
          recordToEdit.newHifzRange || 
          recordToEdit.new_memorization_pos || 
          recordToEdit.newMemorizationPos
        ),
        oldHifzMurajahRange: parsePosition(
          recordToEdit.oldHifzMurajahRange || 
          recordToEdit.review_pos || 
          recordToEdit.reviewPos
        ),
        newHifzMurajahRange: parsePosition(
          recordToEdit.newHifzMurajahRange || 
          recordToEdit.new_hifz_murajah_pos || 
          recordToEdit.newHifzMurajahPos
        ),
        nextHalaqaNewHifzRange: parsePosition(
          recordToEdit.nextHalaqaNewHifzRange || 
          recordToEdit.assignment_pos || 
          recordToEdit.assignmentPos
        ),
        nextHalaqaOldHifzMurajahRange: parsePosition(
          recordToEdit.nextHalaqaOldHifzMurajahRange || 
          recordToEdit.next_halaqa_old_murajah_pos || 
          recordToEdit.nextHalaqaOldMurajahPos
        ),
        nextHalaqaNewHifzMurajahRange: parsePosition(
          recordToEdit.nextHalaqaNewHifzMurajahRange || 
          recordToEdit.next_halaqa_new_murajah_pos || 
          recordToEdit.nextHalaqaNewMurajahPos
        ),
        // Évaluations
        evalNewHifz: recordToEdit.evalNewHifz || 'NotDone',
        evalOldHifzMurajah: recordToEdit.evalOldHifzMurajah || 'NotDone',
        evalNewHifzMurajah: recordToEdit.evalNewHifzMurajah || 'NotDone'
      };
      
      // Charger dans le formulaire
      loadRecordDataIntoForm(recordData);
      
    } else {
      // Mode ajout : réinitialiser complètement
      console.log('🔍 openQuranRecordModal - Mode ajout');
      
      setEditingRecordId(null);
      
      // Réinitialiser les évaluations
      setEvalNewHifz('NotDone');
      setEvalOldHifzMurajah('NotDone');
      setEvalNewHifzMurajah('NotDone');
      setNewMemorizationEvaluation('لم يتم');
      setReviewEvaluation('لم يتم');
      setNewHifzMurajahEvaluation('لم يتم');
      
      // Réinitialiser newQuranRecord
      setNewQuranRecord({
        studentId: '',
        totalHizb: '',
        newHifzTxt: '',
        oldHifzMurajahTxt: '',
        newHifzMurajahText: '',
        nextHalaqaNewHifzText: '',
        nextHalaqaOldHifzMurajahText: '',
        nextHalaqaNewHifzMurajahText: '',
        note: '',
        startPoint: 'الناس'
      });
      // ✅ Réinitialiser repeatNewHifz
      setRepeatNewHifz(0);
    }
    
    // Charger les étudiants et sourates
    setLoadingModalStudents(true);
    try {
      // ✅ Rafraîchir quranRecords avant de filtrer pour avoir les données les plus récentes
      if (!recordToEdit && onRefreshQuranRecords) {
        await onRefreshQuranRecords();
        // Attendre un peu pour que React mette à jour les props
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      const studentsResponse = await onGetStudents();
      if (studentsResponse.success) {
        const studentsData = studentsResponse.data || [];
        
        // ✅ Filtrer les étudiants qui n'ont pas de "آخر جلسة" du jour même (uniquement en mode ajout)
        if (!recordToEdit) {
          // ✅ Jour « aujourd'hui » dans le fuseau de l'ASSOCIATION (et non celui du navigateur)
          const todayString = todayInTz(timezone);
          
          // Utiliser quranRecords qui devrait être mis à jour après onRefreshQuranRecords
          // Si la ref est plus récente, l'utiliser
          const recordsToUse = quranRecordsRef.current.length > 0 && quranRecordsRef.current.length >= quranRecords.length 
            ? quranRecordsRef.current 
            : quranRecords;
          
          
          // Filtrage étape 1 - Filtrer par professeur (uniquement pour AdminDashboard)
          let studentsToFilter = studentsData;
          if (teachers && teachers.length > 0 && currentTeacherFilter && currentTeacherFilter !== 'all') {
            const teacherId = parseInt(currentTeacherFilter);
            
            // Utiliser onGetStudentIdsByTeacher si disponible (source de vérité: student_teachers)
            if (onGetStudentIdsByTeacher) {
              try {
                const studentIdsResponse = await onGetStudentIdsByTeacher(teacherId);
                if (studentIdsResponse.success && studentIdsResponse.data && studentIdsResponse.data.length > 0) {
                  const studentIdsForTeacher = new Set(studentIdsResponse.data);
                  
                  // Filtrer les étudiants pour ne garder que ceux du professeur
                  studentsToFilter = studentsData.filter((student: any) => 
                    studentIdsForTeacher.has(student.id)
                  );
                }
              } catch (error) {
                console.error('Erreur lors de la récupération des étudiants du professeur:', error);
              }
            } else {
              // Fallback: Utiliser quranRecords
              const studentIdsForTeacher = new Set(
                recordsToUse
                  .filter(r => {
                    const recordAny = r as any;
                    const recordTeacherId = r.teacherId || recordAny.teacher_id;
                    return recordTeacherId === teacherId || parseInt(String(recordTeacherId)) === teacherId;
                  })
                  .map(r => {
                    const recordAny = r as any;
                    return r.studentId || recordAny.student_id;
                  })
                  .filter(id => id !== undefined && id !== null)
              );
              
              studentsToFilter = studentsData.filter((student: any) => 
                studentIdsForTeacher.has(student.id)
              );
            }
          }
          
          // Filtrage étape 2 - Exclure les étudiants qui ont déjà un record aujourd'hui
          const studentsWithoutTodayRecord = studentsToFilter.filter((student: any) => {
            const studentId = parseInt(student.id) || student.id;
            
            // Vérifier si l'étudiant a un enregistrement du jour même
            const hasTodayRecord = recordsToUse.some(record => {
              const recordDate = record.modifiedDate || record.createdDate;
              if (!recordDate) return false;
              
              // Normaliser la date : convertir en date locale pour la comparaison
              let recordDateLocal: Date;
              if (typeof recordDate === 'string') {
                recordDateLocal = new Date(recordDate);
              } else {
                recordDateLocal = new Date(recordDate as any);
              }
              
              if (isNaN(recordDateLocal.getTime())) return false;
              
              // Extraire la date locale (sans l'heure)
              const recordDateLocalOnly = new Date(recordDateLocal.getFullYear(), recordDateLocal.getMonth(), recordDateLocal.getDate());
              const recordDateString = `${recordDateLocalOnly.getFullYear()}-${String(recordDateLocalOnly.getMonth() + 1).padStart(2, '0')}-${String(recordDateLocalOnly.getDate()).padStart(2, '0')}`;
              
              // Comparer les IDs (normaliser en nombre pour la comparaison)
              const recordStudentId = record.studentId != null 
                ? (parseInt(String(record.studentId)) || record.studentId)
                : null;
              
              // Comparer les dates et IDs
              if (recordDateString !== todayString) return false;
              
              return recordStudentId !== null && recordStudentId === studentId;
            });
            
            return !hasTodayRecord;
          });
          
          // Mapper les étudiants filtrés
          const mappedStudents = studentsWithoutTodayRecord.map((student: any) => ({
            ...student,
            id: student.id,
            studentName: GLOBAL_UTILS.cleanText(student.student_name || student.studentName || `طالب ${student.id}`),
            student_name: GLOBAL_UTILS.cleanText(student.student_name || student.studentName || `طالب ${student.id}`),
            level: GLOBAL_UTILS.cleanText(student.level || ''),
            averageGrade: student.average || student.averageGrade || 0,
            average: student.average || student.averageGrade || 0,
            hizbCount: 0
          }));
          
          setModalStudents(mappedStudents);
        } else {
          // En mode édition, garder tous les étudiants
          const mappedStudents = studentsData.map((student: any) => ({
            ...student,
            id: student.id,
            studentName: GLOBAL_UTILS.cleanText(student.student_name || student.studentName || `طالب ${student.id}`),
            student_name: GLOBAL_UTILS.cleanText(student.student_name || student.studentName || `طالب ${student.id}`),
            level: GLOBAL_UTILS.cleanText(student.level || ''),
            averageGrade: student.average || student.averageGrade || 0,
            average: student.average || student.averageGrade || 0,
            hizbCount: 0
          }));
          setModalStudents(mappedStudents);
        }
      }
      if (surahs.length === 0) {
        await loadSurahs();
      }
    } catch (error) {
      console.error('Erreur lors du chargement des étudiants:', error);
    } finally {
      setLoadingModalStudents(false);
    }
    
    // Ouvrir le modal
    setShowQuranRecordModal(true);
  }, [onGetStudents, loadSurahs, surahs.length, extractOriginalNoteText, loadRecordDataIntoForm, quranRecords, onRefreshQuranRecords, teachers, currentTeacherFilter, onGetStudentIdsByTeacher, timezone]);

  // Fonction pour rafraîchir - force le rechargement complet sans optimisation
  const handleRefresh = useCallback(async () => {
    setRefreshing('quran');
    try {
      console.log('🔄 QuranTrackingSection - handleRefresh appelé - FORCE REFRESH');
      
      // Vider tous les caches avant le rechargement
      latestRecordCacheRef.current.clear();
      nextSessionCacheRef.current.clear(); // ✅ Nettoyer aussi le cache de quran_next_session
      setNextSessionDataLoaded(new Map());
      quranRecordsRef.current = [];
      studentsRef.current = [];
      
      // Forcer le rechargement des données
      await onRefreshQuranRecords();
      
      // Recharger aussi les étudiants si la fonction est disponible
      if (onGetStudents) {
        try {
          const studentsResponse = await onGetStudents();
          if (studentsResponse.success && studentsResponse.data) {
            // Mettre à jour studentsRef avec les nouveaux étudiants
            // Note: Le state 'students' sera mis à jour par le parent, mais on met à jour la ref immédiatement
            // pour éviter que le useMemo utilise une ref vide
            studentsRef.current = studentsResponse.data.map((student: any) => ({
              ...student,
              id: student.id,
              studentName: GLOBAL_UTILS.cleanText(student.student_name || student.studentName || `طالب ${student.id}`),
              student_name: GLOBAL_UTILS.cleanText(student.student_name || student.studentName || `طالب ${student.id}`),
              level: GLOBAL_UTILS.cleanText(student.level || ''),
              averageGrade: student.average || student.averageGrade || 0,
              average: student.average || student.averageGrade || 0,
              hizbCount: 0
            }));
          }
        } catch (error) {
          console.warn('⚠️ QuranTrackingSection - Erreur lors du rechargement des étudiants:', error);
        }
      }
      
      if (onRefreshStats) {
        await onRefreshStats();
      }
    } catch (error) {
      console.error('❌ QuranTrackingSection - Erreur lors du rafraîchissement:', error);
      alert('خطأ في تحديث البيانات');
    } finally {
      setRefreshing(null);
    }
  }, [onRefreshQuranRecords, onRefreshStats, onGetStudents]);

  // Handler pour le changement de note
  // Prendre le texte tel quel, sans aucun traitement
  // L'évaluation est maintenant dans un champ séparé
  const handleNoteChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNewQuranRecord(prev => ({ ...prev, note: e.target.value }));
  }, []);

  // Styles par défaut
  const defaultStyles = useMemo(() => ({
    card: {
      backgroundColor: 'white',
      borderRadius: '12px',
      padding: '24px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
      border: '1px solid #f0f0f0'
    },
    button: {
      padding: '10px 20px',
      borderRadius: '8px',
      border: 'none',
      fontSize: '14px',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.2s'
    },
    primaryButton: {
      backgroundColor: '#9333ea',
      color: 'white'
    },
    formGroup: {
      marginBottom: '16px'
    },
    label: {
      display: 'block',
      marginBottom: '8px',
      fontSize: '14px',
      fontWeight: '500',
      color: '#374151'
    },
    input: {
      width: '100%',
      padding: '10px',
      borderRadius: '8px',
      border: '1px solid #e5e7eb',
      fontSize: '14px'
    },
    textarea: {
      width: '100%',
      padding: '10px',
      borderRadius: '8px',
      border: '1px solid #e5e7eb',
      fontSize: '14px',
      fontFamily: 'inherit',
      resize: 'vertical' as const
    },
    select: {
      width: '100%',
      padding: '10px',
      borderRadius: '8px',
      border: '1px solid #e5e7eb',
      fontSize: '14px',
      backgroundColor: 'white'
    },
    buttonGroup: {
      display: 'flex',
      gap: '12px',
      justifyContent: 'flex-end',
      marginTop: '20px'
    },
    flexButton: {
      flex: 1
    },
    secondaryButton: {
      backgroundColor: '#e5e7eb',
      color: '#374151'
    },
    modal: {
      position: 'fixed' as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    },
    modalContent: {
      backgroundColor: 'white',
      borderRadius: '12px',
      padding: '24px',
      maxWidth: '600px',
      width: '90%',
      maxHeight: '90vh',
      overflowY: 'auto' as const
    }
  }), []);

  const finalStyles = useMemo(() => styles || defaultStyles, [styles, defaultStyles]);
  
  // Utiliser une ref pour finalStyles
  const finalStylesRef = React.useRef(finalStyles);
  React.useEffect(() => {
    finalStylesRef.current = finalStyles;
  }, [finalStyles]);
  
  // Mémoriser les valeurs précédentes pour détecter les changements
  const prevDepsRef = React.useRef({
    studentsIds: '',
    quranRecordsIds: '',
    currentStudentFilter: '',
    currentTeacherFilter: '',
    t: null as any,
    finalStyles: null as any
  });
  
  // Cache pour mémoriser les objets latestRecord par student_id pour éviter de créer de nouveaux objets à chaque rendu
  const latestRecordCacheRef = React.useRef<Map<number, any>>(new Map());
  // Cache pour les données de quran_next_session (studentId -> nextSession data)
  const nextSessionCacheRef = React.useRef<Map<number, any>>(new Map());
  // État pour forcer le re-render quand les données de quran_next_session sont récupérées
  const [nextSessionDataLoaded, setNextSessionDataLoaded] = React.useState<Map<number, boolean>>(new Map());
  
  // Log pour détecter quelle dépendance change (seulement pour les dépendances du useMemo)
  React.useEffect(() => {
    const prev = prevDepsRef.current;
    const changes: string[] = [];
    
    if (prev.studentsIds !== studentsIds) {
      changes.push(`studentsIds: "${prev.studentsIds}" -> "${studentsIds}"`);
    }
    if (prev.quranRecordsIds !== quranRecordsIds) {
      changes.push(`quranRecordsIds: "${prev.quranRecordsIds}" -> "${quranRecordsIds}"`);
    }
    if (prev.currentStudentFilter !== currentStudentFilter) {
      changes.push(`currentStudentFilter: "${prev.currentStudentFilter}" -> "${currentStudentFilter}"`);
    }
    if (prev.currentTeacherFilter !== currentTeacherFilter) {
      changes.push(`currentTeacherFilter: "${prev.currentTeacherFilter}" -> "${currentTeacherFilter}"`);
    }
    
    // Ne pas logger t et finalStyles car ils ne sont plus dans les dépendances du useMemo
    
    // Ne logger et mettre à jour la ref que si quelque chose a vraiment changé
    if (changes.length > 0) {
      console.log('🔍 QuranTrackingSection - Dépendances changées:', changes);
      prevDepsRef.current = {
        studentsIds,
        quranRecordsIds,
        currentStudentFilter,
        currentTeacherFilter,
        t,
        finalStyles
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentsIds, quranRecordsIds, currentStudentFilter, currentTeacherFilter]);

  // ✅ Récupérer les données de quran_next_session pour tous les étudiants visibles
  React.useEffect(() => {
    if (!onGetNextSessionForStudent) return;
    
    // Filtrer les étudiants selon les filtres actifs
    const visibleStudents = students.filter(student => {
      if (currentStudentFilter !== 'all' && currentStudentFilter !== undefined) {
        return student.id.toString() === currentStudentFilter;
      }
      return true;
    });
    
    // Récupérer les données de quran_next_session pour chaque étudiant visible
    // ⚠️ NOTE: Ne pas récupérer ici car ParentDashboard.refreshQuranRecords() le fait déjà
    // Ce useEffect est gardé pour les cas où le composant est utilisé sans ParentDashboard
    // mais on ne récupère que si les données ne sont pas déjà en cache
    visibleStudents.forEach(async (student) => {
      const studentId = student.id;
      // ✅ Vérifier d'abord si les données sont déjà en cache
      if (nextSessionCacheRef.current.has(studentId)) {
        // Données déjà en cache, ne pas refaire l'appel
        return;
      }
      
      try {
        const response = await onGetNextSessionForStudent(studentId);
        if (response.success && response.data) {
          // Stocker dans le cache (écrase l'ancienne valeur si elle existe)
          nextSessionCacheRef.current.set(studentId, response.data);
          // Forcer le re-render pour cet étudiant
          setNextSessionDataLoaded(prev => {
            const newMap = new Map(prev);
            newMap.set(studentId, true);
            return newMap;
          });
        }
      } catch (error) {
        console.warn(`⚠️ Erreur lors de la récupération de quran_next_session pour studentId ${studentId}:`, error);
      }
    });
  }, [students, currentStudentFilter, onGetNextSessionForStudent, quranRecordsIds]);

  // Fonction helper pour formater la partie versets selon les règles spécifiées
  // Cette fonction retourne uniquement la partie "إلى الآية X" ou "إلى آخرها" ou "كاملة"
  // Wrapper pour formatQuranSelection (utilise la fonction globale)
  const formatQuranSelectionLocal = useCallback((selection: QuranSelection, type: 'newMemorization' | 'review' | 'assignment'): string => {
    return formatQuranSelection(
      selection,
      type,
      surahs,
      verseCounts
    );
  }, [surahs, verseCounts]);

  // Valider une sélection de Coran
  const validateQuranSelection = useCallback((selection: QuranSelection): { valid: boolean; error?: string } => {
    if (!selection.startSurah || !selection.endSurah) {
      return { valid: false, error: 'يجب اختيار سورة البداية وسورة النهاية' };
    }

    const maxStartVerse = verseCounts[selection.startSurah] || 0;
    const maxEndVerse = verseCounts[selection.endSurah] || 0;

    if (maxStartVerse === 0 || maxEndVerse === 0) {
      return { valid: true };
    }

    if (selection.startVerse > 0) {
      if (selection.startVerse < 1) {
        return { valid: false, error: 'الآية من يجب أن تكون أكبر من 0' };
      }
      if (selection.startVerse > maxStartVerse) {
        return { valid: false, error: `الآية من يجب أن تكون أقل من أو تساوي ${maxStartVerse} (عدد آيات السورة)` };
      }
    }

    if (selection.endVerse > 0) {
      if (selection.endVerse < 1) {
        return { valid: false, error: 'الآية إلى يجب أن تكون أكبر من 0' };
      }
      if (selection.endVerse > maxEndVerse) {
        return { valid: false, error: `الآية إلى يجب أن تكون أقل من أو تساوي ${maxEndVerse} (عدد آيات السورة)` };
      }
    }

    // ❌ VALIDATION DÉSACTIVÉE - Permet de sélectionner des versets dans n'importe quel ordre
    // if (selection.startSurah === selection.endSurah) {
    //   if (selection.startVerse > 0 && selection.endVerse > 0) {
    //     if (selection.endVerse < selection.startVerse) {
    //       return { valid: false, error: 'الآية إلى يجب أن تكون أكبر من أو تساوي الآية من' };
    //     }
    //   }
    // }

    return { valid: true };
  }, [verseCounts]);

  // Calculer le nombre de hizb à partir d'une sélection
  // ✅ AMÉLIORÉ : Gère correctement les ordres inversés en trouvant le point le plus avancé
  const calculateHizbFromSelection = useCallback(async (selection: QuranSelection): Promise<number> => {
    if (!selection.startSurah || !selection.endSurah) return 0;

    try {
      setCalculatingHizb(true);
      const baseUrl = ENV_CONFIG.apiBaseUrl;
      const startPoint = newQuranRecord.startPoint || 'الناس';

      // ✅ NOUVEAU : Déterminer quelle sourate/verset est le plus avancé dans le parcours
      let mostAdvancedSurah: number;
      let mostAdvancedVerse: number;
      
      if (startPoint === 'الناس') {
        // Décroissant (114→1) : le plus petit numéro est le plus avancé
        mostAdvancedSurah = Math.min(selection.startSurah, selection.endSurah);
        
        // Si même sourate, déterminer le verset le plus avancé
        if (selection.startSurah === selection.endSurah) {
          const verse1 = selection.startVerse > 0 ? selection.startVerse : 0;
          const verse2 = selection.endVerse > 0 ? selection.endVerse : 0;
          
          // Si l'un des deux est 0, prendre l'autre (0 signifie fin de sourate)
          if (verse1 === 0 && verse2 === 0) {
            mostAdvancedVerse = 0; // Les deux à 0, on prendra la fin de sourate
          } else if (verse1 === 0) {
            mostAdvancedVerse = verse2;
          } else if (verse2 === 0) {
            mostAdvancedVerse = verse1;
          } else {
            // Prendre le plus petit verset (plus avancé en décroissant)
            mostAdvancedVerse = Math.min(verse1, verse2);
          }
        } else {
          // Sourates différentes : utiliser le verset de la sourate la plus avancée
          if (selection.startSurah === mostAdvancedSurah) {
            mostAdvancedVerse = selection.startVerse > 0 ? selection.startVerse : 0;
          } else {
            mostAdvancedVerse = selection.endVerse > 0 ? selection.endVerse : 0;
          }
        }
      } else {
        // Croissant (2→114) : le plus grand numéro est le plus avancé
        mostAdvancedSurah = Math.max(selection.startSurah, selection.endSurah);
        
        // Si même sourate, déterminer le verset le plus avancé
        if (selection.startSurah === selection.endSurah) {
          const verse1 = selection.startVerse > 0 ? selection.startVerse : 0;
          const verse2 = selection.endVerse > 0 ? selection.endVerse : 0;
          
          // Si l'un des deux est 0, prendre l'autre (0 signifie fin de sourate)
          if (verse1 === 0 && verse2 === 0) {
            mostAdvancedVerse = 0; // Les deux à 0, on prendra la fin de sourate
          } else if (verse1 === 0) {
            mostAdvancedVerse = verse2;
          } else if (verse2 === 0) {
            mostAdvancedVerse = verse1;
          } else {
            // Prendre le plus grand verset (plus avancé en croissant)
            mostAdvancedVerse = Math.max(verse1, verse2);
          }
        } else {
          // Sourates différentes : utiliser le verset de la sourate la plus avancée
          if (selection.startSurah === mostAdvancedSurah) {
            mostAdvancedVerse = selection.startVerse > 0 ? selection.startVerse : 0;
          } else {
            mostAdvancedVerse = selection.endVerse > 0 ? selection.endVerse : 0;
          }
        }
      }
      
      // Si mostAdvancedVerse = 0, utiliser le dernier verset de la sourate
      if (mostAdvancedVerse === 0) {
        if (!verseCounts[mostAdvancedSurah]) {
          await loadVerseCount(mostAdvancedSurah);
        }
        mostAdvancedVerse = verseCounts[mostAdvancedSurah] || 0;
        if (mostAdvancedVerse === 0) return 0;
      }

      // Appeler l'API avec le point le plus avancé
      const response = await fetch(
        `${baseUrl}/api/quran/verses/range?surah_pos=${mostAdvancedSurah}&start=${mostAdvancedVerse}&end=${mostAdvancedVerse}`
      );
      const data = await response.json();
      
      if (!data.success || !data.data || !data.data.verses || data.data.verses.length === 0) {
        return 0;
      }

      const lastVerse = data.data.verses[0];
      if (!lastVerse.hizb_quart) {
        return 0;
      }

      const hizbFromQuart = lastVerse.hizb_quart / 4;
      let totalHizb: number;
      
      if (startPoint === 'الناس') {
        totalHizb = 60 - hizbFromQuart;  // Total depuis la fin du Coran
      } else {
        totalHizb = hizbFromQuart;  // Total depuis le début du Coran
      }

      return Math.round(totalHizb * 100) / 100;
    } catch (error) {
      console.error('❌ Erreur lors du calcul des hizb:', error);
      return 0;
    } finally {
      setCalculatingHizb(false);
    }
  }, [verseCounts, newQuranRecord.startPoint, loadVerseCount]);


  // Calculer et mettre à jour le total_hizb automatiquement
  // ✅ Recalculer TOUJOURS quand la sélection change (même en mode édition)
  useEffect(() => {
    const updateTotalHizb = async () => {
      if (newMemorizationSelection.startSurah > 0 && newMemorizationSelection.endSurah > 0) {
        const calculatedHizb = await calculateHizbFromSelection(newMemorizationSelection);
        if (calculatedHizb >= 0) {
          setNewQuranRecord(prev => ({
            ...prev,
            totalHizb: calculatedHizb.toString()
          }));
        }
      } else {
        setNewQuranRecord(prev => ({
          ...prev,
          totalHizb: ''
        }));
      }
    };

    const timeoutId = setTimeout(() => {
      updateTotalHizb();
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [newMemorizationSelection, calculateHizbFromSelection, editingRecordId]);

  // ✅ SUPPRIMÉ - validateAndFixSelection : Plus de validation basée sur startPoint
  // L'utilisateur peut maintenant choisir n'importe quelle combinaison de sourates (début/fin)
  // Le tri dans les listes déroulantes continue d'utiliser startPoint pour l'affichage uniquement

  // Ouvrir le modal d'édition une fois que les sourates sont chargées
  useEffect(() => {
    if (editingRecordId && surahs.length > 0 && !showQuranRecordModal) {
      // Les sourates sont chargées, ouvrir le modal maintenant
      console.log('🔍 QuranTrackingSection - useEffect: Ouverture du modal car les sourates sont chargées');
      setShowQuranRecordModal(true);
    }
  }, [editingRecordId, surahs.length, showQuranRecordModal]);

  // Charger le paramètre targetRepeatNewHifzCount depuis l'API
  useEffect(() => {
    const fetchTargetRepeatNewHifzCount = async () => {
      try {
        const apiService = ApiServiceAdmin.getInstance();
        const response = await apiService.getParameterByName('targetRepeatNewHifzCount');
        if (response.success && response.data) {
          const count = parseInt(response.data.value) || 15;
          setTargetRepeatNewHifzCount(count);
          console.log('✅ Paramètre targetRepeatNewHifzCount chargé:', count);
        }
      } catch (error) {
        console.error('Erreur lors du chargement du paramètre targetRepeatNewHifzCount:', error);
        // Garder la valeur par défaut de 15
      }
    };
    
    fetchTargetRepeatNewHifzCount();
  }, []);

  // Charger le paramètre maxPreviousSessions depuis l'API
  // Nombre max de "sessions précédentes" affichées par élève (0 = illimité).
  // Le suivi de mémorisation est longitudinal (plusieurs années) : on limite l'affichage, pas les données.
  useEffect(() => {
    const fetchMaxPreviousSessions = async () => {
      try {
        const apiService = ApiServiceAdmin.getInstance();
        const response = await apiService.getParameterByName('maxPreviousSessions');
        if (response.success && response.data) {
          const parsed = parseInt(response.data.value, 10);
          if (!isNaN(parsed) && parsed >= 0) {
            setMaxPreviousSessions(parsed);
            console.log('✅ Paramètre maxPreviousSessions chargé:', parsed);
          }
        }
      } catch (error) {
        console.error('Erreur lors du chargement du paramètre maxPreviousSessions:', error);
        // Garder la valeur par défaut de 10
      }
    };

    fetchMaxPreviousSessions();
  }, []);

  // ✅ NOUVEAU : Réinitialiser repeatNewHifz à 0 dans la modale si l'évaluation devient non positive
  useEffect(() => {
    const isPositiveEval = evalNewHifz === 'Excellent' || evalNewHifz === 'Good' || evalNewHifz === 'Moyen';
    
    // Si l'évaluation n'est plus positive ET que repeatNewHifz > 0
    if (!isPositiveEval && repeatNewHifz > 0) {
      console.log('⚠️ QuranTrackingSection - Modale : Réinitialisation de repeatNewHifz à 0 car évaluation non positive:', {
        evalNewHifz,
        repeatNewHifz
      });
      setRepeatNewHifz(0);
    }
  }, [evalNewHifz, repeatNewHifz]);

  // Fonction helper pour générer un composant de sélection de Coran
  const renderQuranSelection = useCallback((
    label: string,
    selection: QuranSelection,
    setSelection: React.Dispatch<React.SetStateAction<QuranSelection>>,
    type: 'newMemorization' | 'review' | 'assignment',
    required: boolean = false,
    evaluation?: string,
    setEvaluation?: React.Dispatch<React.SetStateAction<string>>
  ) => {
    const maxVerse = selection.startSurah > 0 ? (verseCounts[selection.startSurah] || 0) : 0;
    const maxEndVerse = selection.endSurah > 0 ? (verseCounts[selection.endSurah] || 0) : 0;
    const validation = validateQuranSelection(selection);
    
    const getSortedSurahs = () => {
      if (!surahs.length) return [];
      const startPoint = newQuranRecord.startPoint || 'الناس';
      if (startPoint === 'الناس') {
        return [...surahs].sort((a, b) => b.pos - a.pos);
      } else if (startPoint === 'البقرة') {
        const sorted = [...surahs].sort((a, b) => {
          if (a.pos >= 2 && b.pos >= 2) {
            return a.pos - b.pos;
          }
          if (a.pos === 1) return 1;
          if (b.pos === 1) return -1;
          return a.pos - b.pos;
        });
        return sorted;
      }
      return surahs;
    };
    
    const sortedSurahs = getSortedSurahs();
    
    // ✅ SUPPRIMÉ - isSurahBefore : Plus de validation basée sur startPoint
    
    const getEndSurahs = () => {
      // ❌ FILTRAGE DÉSACTIVÉ - Toutes les sourates sont disponibles sans restriction
      // Retourner la liste complète des sourates triées
      return sortedSurahs;
      
      /* CODE ORIGINAL DÉSACTIVÉ - Filtrait selon l'ordre du startPoint
      if (!selection.startSurah || selection.startSurah === 0) {
        return sortedSurahs;
      }
      
      const startPoint = newQuranRecord.startPoint || 'الناس';
      
      return sortedSurahs.filter(surah => {
        // Toujours permettre la même sourate
        if (surah.pos === selection.startSurah) return true;
        
        // TOUJOURS inclure la sourate de fin si elle est définie (pour permettre le chargement de données existantes)
        // Cette logique permet de charger des sélections qui peuvent traverser plusieurs sourates
        // même si elles ne respectent pas strictement l'ordre défini par start_point
        if (selection.endSurah > 0 && surah.pos === selection.endSurah) {
          return true;
        }
        
        // Filtrer selon l'ordre défini par start_point
        if (startPoint === 'الناس') {
          // Ordre décroissant: on ne peut choisir que les sourates <= startSurah
          // (car les sourates plus grandes sont "avant" dans l'ordre décroissant)
          return surah.pos <= selection.startSurah;
        } else if (startPoint === 'البقرة') {
          // Ordre croissant: on ne peut choisir que les sourates >= startSurah
          // (car les sourates plus petites sont "avant" dans l'ordre croissant)
          return surah.pos >= selection.startSurah;
        }
        
        // Par défaut, utiliser la fonction isSurahBefore
        return isSurahBefore(selection.startSurah, surah.pos);
      });
      */
    };
    
    const endSurahs = getEndSurahs();
    
    const typeStyles = {
      newMemorization: {
        borderColor: '#10b981',
        backgroundColor: '#ecfdf5',
        labelColor: '#059669',
        accentColor: '#10b981'
      },
      review: {
        borderColor: '#3b82f6',
        backgroundColor: '#eff6ff',
        labelColor: '#2563eb',
        accentColor: '#3b82f6'
      },
      assignment: {
        borderColor: '#f59e0b',
        backgroundColor: '#fffbeb',
        labelColor: '#d97706',
        accentColor: '#f59e0b'
      }
    };
    
    const style = typeStyles[type];
    
    if (selection.startSurah > 0 && !verseCounts[selection.startSurah]) {
      loadVerseCount(selection.startSurah);
    }
    if (selection.endSurah > 0 && !verseCounts[selection.endSurah]) {
      loadVerseCount(selection.endSurah);
    }

    const handleStartVerseChange = (value: string) => {
      const cleanedValue = value.trim();
      let verseNum = 0;
      if (cleanedValue === '') {
        verseNum = 0;
      } else {
        const numericValue = cleanedValue.replace(/[^0-9]/g, '');
        if (numericValue) {
          verseNum = parseInt(numericValue) || 0;
        }
      }
      let newSelection = { ...selection, startVerse: verseNum };
      if (selection.startSurah === selection.endSurah && verseNum > 0 && selection.endVerse > 0) {
        if (verseNum > selection.endVerse) {
          newSelection.endVerse = verseNum;
        }
      }
      setSelection(newSelection);
    };

    const handleEndVerseChange = (value: string) => {
      const cleanedValue = value.trim();
      let verseNum = 0;
      if (cleanedValue === '') {
        verseNum = 0;
      } else {
        const numericValue = cleanedValue.replace(/[^0-9]/g, '');
        if (numericValue) {
          verseNum = parseInt(numericValue) || 0;
        }
      }
      let newSelection = { ...selection, endVerse: verseNum };
      if (selection.startSurah === selection.endSurah && verseNum > 0 && selection.startVerse > 0) {
        if (verseNum < selection.startVerse) {
          console.warn('الآية إلى يجب أن تكون أكبر من أو تساوي الآية من');
        }
      }
      setSelection(newSelection);
    };
    
    return (
      <div style={{
        ...finalStyles.formGroup,
        marginBottom: '12px',
        padding: '12px',
        backgroundColor: style.backgroundColor,
        border: `2px solid ${style.borderColor}`,
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
      }}>
        <label style={{
          ...finalStyles.label,
          fontSize: '15px',
          marginBottom: '10px',
          color: style.labelColor,
          fontWeight: '600',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '8px',
          direction: 'rtl'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', direction: 'rtl' }}>
            <span style={{
              width: '4px',
              height: '20px',
              backgroundColor: style.accentColor,
              borderRadius: '2px',
              display: 'inline-block'
            }}></span>
            {label}
          </div>
          {(type === 'newMemorization' || type === 'review') && evaluation !== undefined && setEvaluation && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', direction: isRTL ? 'rtl' : 'ltr' }}>
              <span style={{ fontSize: '11px', color: style.labelColor, fontWeight: '500', opacity: 0.8 }}>{qt.evaluation}:</span>
              <select
                value={evaluation}
                onChange={(e) => setEvaluation(e.target.value)}
                style={{
                  padding: '4px 8px',
                  fontSize: '11px',
                  border: `1px solid ${style.borderColor}`,
                  borderRadius: '4px',
                  backgroundColor: 'white',
                  color: style.labelColor,
                  fontWeight: '500',
                  cursor: 'pointer',
                  minWidth: '100px'
                }}
              >
                <option value="ممتاز">{qt.evalExcellent}</option>
                <option value="جيد">{qt.evalGood}</option>
                <option value="متوسط">{qt.evalMoyen}</option>
                <option value="إعادة">{qt.evalRepeat}</option>
                <option value="غائب">{qt.evalAbsent}</option>
                <option value="لم يتم">{qt.evalNotDone}</option>
              </select>
            </div>
          )}
        </label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', direction: 'rtl' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
            <div>
              <SearchableSelectOptions
                label="سورة البداية"
                placeholder="اختر سورة"
                value={selection.startSurah || 0}
                options={sortedSurahs}
                onSelect={(surahPos) => {
                  let newEndSurah = selection.endSurah;
                  let newEndVerse = selection.endVerse;
                  
                  if (surahPos > 0) {
                    // ✅ Plus de validation basée sur startPoint - l'utilisateur peut choisir n'importe quelle combinaison
                    if (selection.endSurah === surahPos) {
                      // Même sourate, garder le verset de fin
                      newEndSurah = surahPos;
                      newEndVerse = selection.endVerse;
                    } else if (selection.endSurah > 0) {
                      // Sourate de fin différente, la garder mais réinitialiser le verset
                      newEndSurah = selection.endSurah;
                      newEndVerse = 0;
                    } else {
                      // Aucune sourate de fin sélectionnée, utiliser la même que la sourate de début
                      newEndSurah = surahPos;
                      newEndVerse = 0;
                    }
                  } else {
                    // Aucune sourate sélectionnée, tout réinitialiser
                    newEndSurah = 0;
                    newEndVerse = 0;
                  }
                  
                  setSelection({
                    ...selection,
                    startSurah: surahPos,
                    startVerse: 0,
                    endSurah: newEndSurah,
                    endVerse: surahPos === newEndSurah ? newEndVerse : 0
                  });
                }}
                loading={loadingSurahs}
                error={surahsError}
                countLabel="سورة"
                style={{ marginBottom: 0 }}
              />
            </div>
            <div>
              <label style={{ fontSize: '12px', color: style.labelColor, marginBottom: '3px', display: 'block', fontWeight: '500' }}>
                الآية من
                {maxVerse > 0 && <span style={{ fontSize: '11px', color: '#9ca3af', marginRight: '4px' }}>(حد أقصى: {maxVerse})</span>}
              </label>
              <input
                type="number"
                min="1"
                max={maxVerse || undefined}
                value={selection.startVerse || ''}
                onChange={(e) => handleStartVerseChange(e.target.value)}
                onBlur={(e) => {
                  const value = e.target.value;
                  const numValue = parseInt(value) || 0;
                  if (numValue > 0) {
                    if (numValue < 1) {
                      handleStartVerseChange('1');
                    } else if (maxVerse > 0 && numValue > maxVerse) {
                      handleStartVerseChange(maxVerse.toString());
                    }
                  }
                }}
                disabled={!selection.startSurah}
                placeholder={maxVerse > 0 ? `1-${maxVerse} (فارغ = بداية السورة)` : '(فارغ = بداية السورة)'}
                style={{
                  ...finalStyles.input,
                  padding: '6px',
                  fontSize: '13px',
                  direction: 'rtl',
                  borderColor: (selection.startVerse > 0 && selection.startVerse > maxVerse && maxVerse > 0) ? '#dc2626' : style.borderColor,
                  borderWidth: '1.5px'
                }}
              />
              {selection.startVerse === 0 && selection.startSurah > 0 && (
                <div style={{ fontSize: '11px', color: '#059669', marginTop: '2px', direction: 'rtl' }}>
                  ✓ سيتم استخدام بداية السورة (الآية 1)
                </div>
              )}
              {selection.startVerse > 0 && maxVerse > 0 && selection.startVerse > maxVerse && (
                <div style={{ fontSize: '11px', color: '#dc2626', marginTop: '2px', direction: 'rtl' }}>
                  الحد الأقصى: {maxVerse}
                </div>
              )}
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
            <div>
              <SearchableSelectOptions
                label="سورة النهاية"
                placeholder="اختر سورة"
                value={selection.endSurah || 0}
                options={endSurahs}
                onSelect={(surahPos) => {
                  // const startPoint = newQuranRecord.startPoint || 'الناس';
                  
                  // ❌ VALIDATION DÉSACTIVÉE - L'utilisateur peut sélectionner n'importe quelle sourate
                  // // Validation supplémentaire selon l'ordre défini par start_point
                  // let isValidSurah = true;
                  // if (selection.startSurah > 0 && surahPos > 0) {
                  //   if (startPoint === 'الناس') {
                  //     // Ordre décroissant: endSurah doit être <= startSurah
                  //     isValidSurah = surahPos <= selection.startSurah;
                  //   } else if (startPoint === 'البقرة') {
                  //     // Ordre croissant: endSurah doit être >= startSurah
                  //     isValidSurah = surahPos >= selection.startSurah;
                  //   } else {
                  //     // Utiliser isSurahBefore comme fallback
                  //     isValidSurah = isSurahBefore(selection.startSurah, surahPos);
                  //   }
                  // }
                  // 
                  // if (!isValidSurah) {
                  //   // Si la sourate n'est pas valide, ne pas mettre à jour
                  //   return;
                  // }
                  
                  let newEndVerse = selection.endVerse;
                  if (surahPos !== selection.startSurah) {
                    newEndVerse = 0;
                  } 
                  // ❌ AJUSTEMENT DÉSACTIVÉ - Permet de garder endVerse même s'il est < startVerse
                  // else {
                  //   if (selection.startVerse > 0 && selection.endVerse > 0 && selection.endVerse < selection.startVerse) {
                  //     newEndVerse = selection.startVerse;
                  //   } else {
                  //     newEndVerse = 0;
                  //   }
                  // }
                  setSelection({
                    ...selection,
                    endSurah: surahPos,
                    endVerse: newEndVerse
                  });
                }}
                loading={loadingSurahs}
                error={surahsError}
                countLabel="سورة"
                style={{ marginBottom: 0 }}
              />
            </div>
            <div>
              <label style={{ fontSize: '12px', color: style.labelColor, marginBottom: '3px', display: 'block', fontWeight: '500' }}>
                الآية إلى
                {maxEndVerse > 0 && <span style={{ fontSize: '11px', color: '#9ca3af', marginRight: '4px' }}>(حد أقصى: {maxEndVerse})</span>}
              </label>
              <input
                type="number"
                min={selection.startSurah === selection.endSurah && selection.startVerse > 0 ? selection.startVerse : 1}
                max={maxEndVerse || undefined}
                value={selection.endVerse || ''}
                onChange={(e) => handleEndVerseChange(e.target.value)}
                onBlur={(e) => {
                  const value = e.target.value;
                  const numValue = parseInt(value) || 0;
                  const minValue = selection.startSurah === selection.endSurah && selection.startVerse > 0 ? selection.startVerse : 1;
                  if (numValue > 0) {
                    if (numValue < minValue) {
                      handleEndVerseChange(minValue.toString());
                    } else if (maxEndVerse > 0 && numValue > maxEndVerse) {
                      handleEndVerseChange(maxEndVerse.toString());
                    }
                  }
                }}
                disabled={!selection.endSurah}
                placeholder={maxEndVerse > 0 ? `${selection.startSurah === selection.endSurah && selection.startVerse > 0 ? selection.startVerse : 1}-${maxEndVerse} (فارغ = نهاية السورة)` : 'الآية (فارغ = نهاية السورة)'}
                style={{
                  ...finalStyles.input,
                  padding: '6px',
                  fontSize: '13px',
                  direction: 'rtl',
                  // ❌ VALIDATION VISUELLE DÉSACTIVÉE - Pas de bordure rouge pour endVerse < startVerse
                  borderColor: (selection.endVerse > 0 && maxEndVerse > 0 && selection.endVerse > maxEndVerse) ? '#dc2626' : style.borderColor,
                  borderWidth: '1.5px'
                }}
              />
              {selection.endVerse === 0 && selection.endSurah > 0 && maxEndVerse > 0 && (
                <div style={{ fontSize: '11px', color: '#059669', marginTop: '2px', direction: 'rtl' }}>
                  ✓ سيتم استخدام نهاية السورة (الآية {maxEndVerse})
                </div>
              )}
              {selection.endVerse > 0 && maxEndVerse > 0 && selection.endVerse > maxEndVerse && (
                <div style={{ fontSize: '11px', color: '#dc2626', marginTop: '2px', direction: 'rtl' }}>
                  الحد الأقصى: {maxEndVerse}
                </div>
              )}
              {/* ❌ MESSAGE D'ERREUR DÉSACTIVÉ - Permet endVerse < startVerse
              {selection.startSurah === selection.endSurah && selection.startVerse > 0 && selection.endVerse > 0 && selection.endVerse < selection.startVerse && (
                <div style={{ fontSize: '11px', color: '#dc2626', marginTop: '2px', direction: 'rtl' }}>
                  يجب أن تكون أكبر من أو تساوي {selection.startVerse}
                </div>
              )}
              */}
            </div>
          </div>
          {!validation.valid && validation.error && (
            <div style={{
              padding: '6px',
              backgroundColor: '#fee2e2',
              borderRadius: '4px',
              fontSize: '12px',
              color: '#dc2626',
              direction: 'rtl',
              marginTop: '4px',
              border: '1px solid #fca5a5'
            }}>
              ⚠️ {validation.error}
            </div>
          )}
          {selection.startSurah > 0 && validation.valid && (
            <div style={{
              padding: '8px',
              backgroundColor: '#ffffff',
              borderRadius: '6px',
              fontSize: '13px',
              color: style.labelColor,
              direction: 'rtl',
              marginTop: '6px',
              border: `1px solid ${style.borderColor}`,
              fontWeight: '500'
            }}>
              {formatQuranSelectionLocal(selection, type)}
            </div>
          )}
        </div>
      </div>
    );
  }, [surahs, verseCounts, newQuranRecord.startPoint, validateQuranSelection, formatQuranSelectionLocal, loadVerseCount, loadingSurahs, surahsError, finalStyles, isRTL, qt.evaluation, qt.evalExcellent, qt.evalGood, qt.evalMoyen, qt.evalRepeat, qt.evalAbsent, qt.evalNotDone]);

  /**
   * Badge pour afficher "تكرار الحفظ الجديد" (style harmonieux)
   * Affiche la dernière session de الحفظ الجديد avec le nombre de répétitions
   */
  const renderRepeatNewHifzBadge = useCallback(() => {
    // ✅ NOUVEAU : Vérifier d'abord si l'évaluation est positive (ممتاز, جيد, متوسط)
    const isPositiveEval = evalNewHifz === 'Excellent' || evalNewHifz === 'Good' || evalNewHifz === 'Moyen';
    
    // Si l'évaluation n'est pas positive, ne rien afficher
    if (!isPositiveEval) {
      return null;
    }
    
    // Récupérer la valeur de la dernière session de الحفظ الجديد
    const lastNewHifz = newMemorizationSelection;
    
    // Formater le texte de la sélection
    let selectionText = '';
    if (lastNewHifz.startSurah > 0) {
      const startSurahData = surahs.find(s => s.pos === lastNewHifz.startSurah);
      const endSurahData = surahs.find(s => s.pos === lastNewHifz.endSurah);
      const startSurahName = startSurahData?.ar || '';
      const endSurahName = endSurahData?.ar || '';
      
      if (lastNewHifz.startSurah === lastNewHifz.endSurah) {
        // Même sourate
        const totalVerses = verseCounts[lastNewHifz.startSurah] || 0;
        const endVerse = lastNewHifz.endVerse || totalVerses;
        
        if (lastNewHifz.startVerse === 1 && endVerse === totalVerses) {
          // Sourate complète
          selectionText = `سورة ${startSurahName} كاملة`;
        } else {
          // Versets spécifiques
          selectionText = `سورة ${startSurahName} من ${lastNewHifz.startVerse} إلى ${endVerse}`;
        }
      } else {
        // Plusieurs sourates
        const endVerse = lastNewHifz.endVerse || verseCounts[lastNewHifz.endSurah] || 0;
        selectionText = `من سورة ${startSurahName} (${lastNewHifz.startVerse}) إلى سورة ${endSurahName} (${endVerse})`;
      }
    }
    
    // Si pas de sélection dans آخر جلسة, ne rien afficher
    if (!selectionText) {
      return null;
    }
    
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        flexWrap: 'wrap',
        padding: '12px',
        backgroundColor: '#f9fafb',
        borderRadius: '6px',
        border: '1px solid #e5e7eb'
      }}>
        {/* Badge titre */}
        <span style={{
          fontSize: '12px',
          fontWeight: '500',
          color: '#0369a1',
          backgroundColor: '#cffafe',
          padding: '4px 8px',
          borderRadius: '4px',
          display: 'inline-block',
          flexShrink: 0
        }}>
          {qt.repeatNewHifzLabel}
        </span>
        
        {/* Texte de la sélection */}
        <span style={{
          fontSize: '13px',
          color: '#334155',
          fontWeight: '500',
          flex: 1
        }}>
          {selectionText}
        </span>
        
        {/* Badge nombre de répétitions */}
        <span style={{
          fontSize: '12px',
          fontWeight: '600',
          color: '#0369a1',
          backgroundColor: '#e0f2fe',
          padding: '4px 10px',
          borderRadius: '6px',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          flexShrink: 0,
          border: '1px solid #7dd3fc'
        }}>
          <span style={{ fontSize: '13px' }}>⭐</span>
          <span>{targetRepeatNewHifzCount} {qt.times}</span>
        </span>
      </div>
    );
  }, [newMemorizationSelection, surahs, verseCounts, targetRepeatNewHifzCount, evalNewHifz, qt.repeatNewHifzLabel, qt.times]);

  // Fonction pour gérer l'ajout/modification d'un enregistrement Coran
  const handleAddQuranRecord = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      // Vérifier si une session existe déjà pour cet étudiant à la même date (seulement si on n'est pas en mode édition)
      if (newQuranRecord.studentId && !editingRecordId) {
        const studentId = parseInt(newQuranRecord.studentId);
        const studentRecords = quranRecords.filter(r => 
          (r.studentId === studentId) ||
          (r.studentName === students.find(s => s.id === studentId)?.studentName)
        );
        
        const recordDate = new Date();
        recordDate.setHours(0, 0, 0, 0);
        
        const existingRecord = studentRecords.find(record => {
          const recordDateToCheck = new Date(record.modifiedDate || record.createdDate || '');
          recordDateToCheck.setHours(0, 0, 0, 0);
          return recordDateToCheck.getTime() === recordDate.getTime();
        });
        
        if (existingRecord) {
          const userChoice = window.confirm(t('sessionExistsToday') || 'يوجد بالفعل جلسة لهذا الطالب اليوم، هل تريد تعديل الجلسة الموجودة؟');
          if (userChoice) {
            setEditingRecordId(existingRecord.id);
            setLoadingModalStudents(true);
            try {
              if (modalStudents.length === 0) {
                const studentsResponse = await onGetStudents();
                if (studentsResponse.success) {
                  const mappedStudents = (studentsResponse.data || []).map((student: any) => ({
                    ...student,
                    id: student.id,
                    studentName: GLOBAL_UTILS.cleanText(student.student_name || student.studentName || `طالب ${student.id}`),
                    student_name: GLOBAL_UTILS.cleanText(student.student_name || student.studentName || `طالب ${student.id}`),
                    level: GLOBAL_UTILS.cleanText(student.level || ''),
                    averageGrade: student.average || student.averageGrade || 0,
                    average: student.average || student.averageGrade || 0,
                    hizbCount: 0
                  }));
                  setModalStudents(mappedStudents);
                }
              }
              
              if (surahs.length === 0) {
                await loadSurahs();
              }
              
              const existingNote = existingRecord.note || '';
              const originalNoteWithoutEvaluation = extractOriginalNoteText(existingNote);
              
              // Charger les valeurs d'évaluation existantes
              const existingEvalNewHifz = existingRecord.evalNewHifz || 'Moyen';
              const existingEvalOldHifzMurajah = existingRecord.evalOldHifzMurajah || 'Moyen';
              const existingEvalNewHifzMurajah = existingRecord.evalNewHifzMurajah || 'NotDone';
              
              // ✅ NOUVEAU : Fonction pour convertir 'Nothing' en 'NotDone' (rétrocompatibilité)
              const normalizeEval = (val: string): 'Excellent' | 'Good' | 'Moyen' | 'Repeat' | 'Absent' | 'NotDone' => {
                if (val === 'Nothing') return 'NotDone';
                return val as 'Excellent' | 'Good' | 'Moyen' | 'Repeat' | 'Absent' | 'NotDone';
              };
              
              setNewQuranRecord({
                studentId: newQuranRecord.studentId,
                totalHizb: (existingRecord.totalHizb != null) ? existingRecord.totalHizb.toString() : '',
                newHifzTxt: existingRecord.newHifzTxt || '',
                oldHifzMurajahTxt: existingRecord.oldHifzMurajahTxt || '',
                newHifzMurajahText: existingRecord.newHifzMurajahText || '',
                nextHalaqaNewHifzText: existingRecord.nextHalaqaNewHifzText || '',
                nextHalaqaOldHifzMurajahText: existingRecord.nextHalaqaOldHifzMurajahText || '',
                nextHalaqaNewHifzMurajahText: existingRecord.nextHalaqaNewHifzMurajahText || '',
                note: originalNoteWithoutEvaluation,
                startPoint: existingRecord.startPoint || 'الناس'
              });
              
              // Synchroniser les évaluations
              setEvalNewHifz(normalizeEval(existingEvalNewHifz));
              setEvalOldHifzMurajah(normalizeEval(existingEvalOldHifzMurajah));
              setEvalNewHifzMurajah(normalizeEval(existingEvalNewHifzMurajah));
              
              // Synchroniser aussi les évaluations arabes pour l'affichage
              setNewMemorizationEvaluation(mapEnglishToArabicEval(existingEvalNewHifz, 'newMemorization'));
              setReviewEvaluation(mapEnglishToArabicEval(existingEvalOldHifzMurajah, 'review'));
              setNewHifzMurajahEvaluation(mapEnglishToArabicEval(existingEvalNewHifzMurajah, 'review'));
              
              if (existingRecord.newHifzRange) {
                const parsePosition = (pos: string | QuranSelection | undefined): QuranSelection => {
                  if (!pos) return { startSurah: 0, startVerse: 0, endSurah: 0, endVerse: 0 };
                  if (typeof pos === 'object' && 'startSurah' in pos) {
                    return pos as QuranSelection;
                  }
                  if (typeof pos === 'string' && pos.includes('-')) {
                    const parts = pos.split('-');
                    if (parts.length === 2) {
                      const [startPart, endPart] = parts;
                      const [startSurah, startVerse] = startPart.split(':').map(Number);
                      const [endSurah, endVerse] = endPart.split(':').map(Number);
                      return { startSurah: startSurah || 0, startVerse: startVerse || 0, endSurah: endSurah || 0, endVerse: endVerse || 0 };
                    }
                  }
                  return { startSurah: 0, startVerse: 0, endSurah: 0, endVerse: 0 };
                };
                
                setNewMemorizationSelection(parsePosition(existingRecord.newHifzRange));
                setReviewSelection(parsePosition(existingRecord.oldHifzMurajahRange));
                setAssignmentSelection(parsePosition(existingRecord.nextHalaqaNewHifzRange));
              }

              setShowQuranRecordModal(true);
            } catch (error) {
              console.error('Erreur lors du chargement des données:', error);
              alert('خطأ في تحميل البيانات');
            } finally {
              setLoadingModalStudents(false);
            }
          }
          return;
        }
      }
      
      // Valider les sélections
      const newMemValidation = validateQuranSelection(newMemorizationSelection);
      const reviewValidation = reviewSelection.startSurah > 0 ? validateQuranSelection(reviewSelection) : { valid: true };
      const assignmentValidation = assignmentSelection.startSurah > 0 ? validateQuranSelection(assignmentSelection) : { valid: true };

      if (!newMemValidation.valid) {
        alert('خطأ في الحفظ الجديد: ' + newMemValidation.error);
        return;
      }
      if (!reviewValidation.valid) {
        alert('خطأ في المراجعة: ' + reviewValidation.error);
        return;
      }
      if (!assignmentValidation.valid) {
        alert('خطأ في الواجب: ' + assignmentValidation.error);
        return;
      }

      // ✅ CALCUL DE TOTAL_HIZB
      // Logique : Si on a une sélection de Coran pour "الحفظ الجديد", calculer automatiquement
      // Sinon, utiliser la valeur du formulaire (pour les cas d'édition sans modification)
      let totalHizbValue = 0;
      
      // Si on a une sélection de mémorisation, TOUJOURS recalculer
      if (newMemorizationSelection.startSurah > 0 && newMemorizationSelection.endSurah > 0) {
        console.log('🔍 Calcul de totalHizb depuis sélection:', newMemorizationSelection);
        try {
          const calculatedHizb = await calculateHizbFromSelection(newMemorizationSelection);
          console.log('🔍 totalHizb calculé:', calculatedHizb);
          if (calculatedHizb >= 0) {
            totalHizbValue = calculatedHizb;
          }
        } catch (error) {
          console.error('❌ Erreur lors du calcul de totalHizb:', error);
        }
      } else if (newQuranRecord.totalHizb) {
        // Pas de sélection, utiliser la valeur du formulaire (mode édition sans modification)
        const parsed = parseFloat(newQuranRecord.totalHizb);
        if (!isNaN(parsed)) {
          totalHizbValue = parsed;
        }
      }
      
      console.log('🔍 totalHizb final:', totalHizbValue);

      // Formater les positions (utilise la fonction globale)
      const newMemPos = newMemorizationSelection.startSurah > 0 
        ? await formatPosition(newMemorizationSelection, verseCounts, loadVerseCount) 
        : null;
      const reviewPos = reviewSelection.startSurah > 0 
        ? await formatPosition(reviewSelection, verseCounts, loadVerseCount) 
        : null;
      const newHifzMurajahPos = newHifzMurajahSelection.startSurah > 0 
        ? await formatPosition(newHifzMurajahSelection, verseCounts, loadVerseCount) 
        : null;
      const assignmentPos = assignmentSelection.startSurah > 0 
        ? await formatPosition(assignmentSelection, verseCounts, loadVerseCount) 
        : null;
      const nextHalaqaOldMurajahPos = nextHalaqaOldMurajahSelection.startSurah > 0 
        ? await formatPosition(nextHalaqaOldMurajahSelection, verseCounts, loadVerseCount) 
        : null;
      const nextHalaqaNewMurajahPos = nextHalaqaNewMurajahSelection.startSurah > 0 
        ? await formatPosition(nextHalaqaNewMurajahSelection, verseCounts, loadVerseCount) 
        : null;
      
      const newMemText = newMemorizationSelection.startSurah > 0 
        ? formatQuranSelectionLocal(newMemorizationSelection, 'newMemorization')
        : newQuranRecord.newHifzTxt;
      const reviewText = reviewSelection.startSurah > 0
        ? formatQuranSelectionLocal(reviewSelection, 'review')
        : newQuranRecord.oldHifzMurajahTxt;
      const newHifzMurajahText = newHifzMurajahSelection.startSurah > 0
        ? formatQuranSelectionLocal(newHifzMurajahSelection, 'review')
        : newQuranRecord.newHifzMurajahText;
      const nextHalaqaOldMurajahText = nextHalaqaOldMurajahSelection.startSurah > 0
        ? formatQuranSelectionLocal(nextHalaqaOldMurajahSelection, 'assignment')
        : newQuranRecord.nextHalaqaOldHifzMurajahText;
      const nextHalaqaNewMurajahText = nextHalaqaNewMurajahSelection.startSurah > 0
        ? formatQuranSelectionLocal(nextHalaqaNewMurajahSelection, 'assignment')
        : newQuranRecord.nextHalaqaNewHifzMurajahText;
      
      // Générer le texte d'évaluation
      const evaluationParts: string[] = [];
      
      // ✅ CORRECTION : Ajouter l'évaluation seulement si != 'لم يتم' (sans préfixe)
      if (newMemText && newMemText.trim() && newMemorizationEvaluation !== 'لم يتم') {
        evaluationParts.push(newMemorizationEvaluation);
      }
      
      // ✅ CORRECTION : Ajouter l'évaluation seulement si != 'لم يتم' (sans préfixe)
      if (reviewText && reviewText.trim() && reviewEvaluation !== 'لم يتم') {
        evaluationParts.push(reviewEvaluation);
      }
      
      // ✅ CORRECTION : Ajouter l'évaluation seulement si != 'لم يتم' (sans préfixe)
      if (newHifzMurajahText && newHifzMurajahText.trim() && newHifzMurajahEvaluation !== 'لم يتم') {
        evaluationParts.push(newHifzMurajahEvaluation);
      }
      
      const evaluationText = evaluationParts.join(' • ');
      let userNoteText = (newQuranRecord.note || '').trim();
      
      // Toujours extraire le texte d'évaluation existant pour éviter les doublons
      // (à la fois en mode ajout et édition)
        userNoteText = extractOriginalNoteText(userNoteText);
      
      // Vérifier aussi si le texte d'évaluation existe déjà dans la note (sans les marqueurs)
      // pour éviter les doublons même si extractOriginalNoteText ne l'a pas détecté
      // Liste de tous les textes d'évaluation possibles
      const evaluationTexts = [
        'ممتاز',
        'جيد',
        'متوسط',
        'إعادة',
        'غائب',
        'لم يتم'
      ];
      
      // Nettoyer le texte utilisateur de tous les textes d'évaluation possibles
      let cleanedUserNote = userNoteText;
      for (const evalText of evaluationTexts) {
        // Supprimer le texte d'évaluation avec " • " avant ou après
        cleanedUserNote = cleanedUserNote.replace(new RegExp(`\\s*•\\s*${evalText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`, 'g'), '');
        cleanedUserNote = cleanedUserNote.replace(new RegExp(`${evalText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*•\\s*`, 'g'), '');
        // Supprimer le texte d'évaluation au début ou à la fin
        cleanedUserNote = cleanedUserNote.replace(new RegExp(`^${evalText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*`, 'g'), '');
        cleanedUserNote = cleanedUserNote.replace(new RegExp(`\\s*${evalText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}$`, 'g'), '');
      }
      
      // Nettoyer les séparateurs multiples et orphelins
      cleanedUserNote = cleanedUserNote.replace(/\s*•\s*•\s*/g, ' • ').trim();
      cleanedUserNote = cleanedUserNote.replace(/^\s*•\s*/, '').trim();
      cleanedUserNote = cleanedUserNote.replace(/\s*•\s*$/, '').trim();
      
      userNoteText = cleanedUserNote;
      
      // ✅ Construire deux versions de la note :
      // 1. noteText : Pour l'affichage dans l'UI (avec évaluations automatiques)
      // 2. userNoteText : Pour la sauvegarde en DB (SANS évaluations automatiques)
      // Les évaluations sont déjà stockées séparément dans evalNewHifz, evalOldHifzMurajah, evalNewHifzMurajah
      const wrappedEvaluationText = wrapEvaluationText(evaluationText);
      let noteText = '';
      if (userNoteText && wrappedEvaluationText) {
        noteText = `${userNoteText} • ${wrappedEvaluationText}`;
      } else if (userNoteText) {
        noteText = userNoteText;
      } else if (wrappedEvaluationText) {
        noteText = wrappedEvaluationText;
      }
      noteText = noteText.trim();
      // Note: noteText est utilisé uniquement pour l'affichage (console.log ci-dessous)
      // La DB reçoit userNoteText (sans les évaluations automatiques)
      
      
      // Utiliser les valeurs d'évaluation actuelles (déjà en anglais)
      // Les champs evalNewHifz, evalOldHifzMurajah et evalNewHifzMurajah sont OPTIONNELS selon l'API
      // Validation conditionnelle : valider seulement si les valeurs sont fournies
      const validEvalValues = ['Excellent', 'Good', 'Moyen', 'Repeat', 'Absent', 'NotDone'];
      
      // Fonction pour valider une valeur d'évaluation
      const isValidEvalValue = (value: string | null | undefined): boolean => {
        return !!(value && typeof value === 'string' && validEvalValues.includes(value.trim()));
      };
      
      // Vérifier et valider evalNewHifz (optionnel)
      let validatedEvalNewHifz: string | null = null;
      if (evalNewHifz && typeof evalNewHifz === 'string' && evalNewHifz.trim() !== '') {
        const trimmedValue = evalNewHifz.trim();
        if (isValidEvalValue(trimmedValue)) {
          validatedEvalNewHifz = trimmedValue;
        }
        // Si la valeur n'est pas valide, on la laisse null (ne sera pas envoyée)
      }
      
      // Vérifier et valider evalOldHifzMurajah (optionnel)
      let validatedEvalOldHifzMurajah: string | null = null;
      if (evalOldHifzMurajah && typeof evalOldHifzMurajah === 'string' && evalOldHifzMurajah.trim() !== '') {
        const trimmedValue = evalOldHifzMurajah.trim();
        if (isValidEvalValue(trimmedValue)) {
          validatedEvalOldHifzMurajah = trimmedValue;
        }
        // Si la valeur n'est pas valide, on la laisse null (ne sera pas envoyée)
      }
      
      // Vérifier et valider evalNewHifzMurajah (optionnel)
      let validatedEvalNewHifzMurajah: string | null = null;
      if (evalNewHifzMurajah && typeof evalNewHifzMurajah === 'string' && evalNewHifzMurajah.trim() !== '') {
        const trimmedValue = evalNewHifzMurajah.trim();
        if (isValidEvalValue(trimmedValue)) {
          validatedEvalNewHifzMurajah = trimmedValue;
        }
        // Si la valeur n'est pas valide, on la laisse null (ne sera pas envoyée)
      }
      
      console.log('🔍 QuranTrackingSection - Validation évaluations:', {
        evalNewHifz,
        validatedEvalNewHifz,
        evalOldHifzMurajah,
        validatedEvalOldHifzMurajah,
        evalNewHifzMurajah,
        validatedEvalNewHifzMurajah
      });
      
      console.log('📝 QuranTrackingSection - Note sauvegardée en DB:', {
        userNoteText: userNoteText,
        noteTextWithEval: noteText,
        info: 'Seul userNoteText est sauvegardé (sans évaluations). Les évaluations sont dans evalNewHifz/evalOldHifzMurajah/evalNewHifzMurajah.'
      });
      
      // ✅ SÉPARATION DES DONNÉES : quran_records vs quran_next_session
      
      // ✅ TOUJOURS récupérer repeatNewHifz depuis les données de "الجلسة القادمة" AVANT la sauvegarde
      // Cette valeur sera sauvegardée dans l'AVANT-DERNIER record de quran_records (qui était "آخر جلسة" avant la création du nouveau)
      // Ordre important: 1) Récupérer depuis quran_next_session, 2) Mettre à jour l'avant-dernier record, 3) Créer le nouveau record, 4) Sauvegarder dans quran_next_session
      let finalRepeatNewHifz = 0;
      if (newQuranRecord.studentId && onGetNextSessionForStudent) {
        try {
          const studentId = parseInt(newQuranRecord.studentId);
          if (studentId > 0) {
            // Vérifier d'abord le cache
            if (nextSessionCacheRef.current.has(studentId)) {
              const cachedNextSession = nextSessionCacheRef.current.get(studentId);
              if (cachedNextSession && cachedNextSession.repeatNewHifz != null) {
                finalRepeatNewHifz = cachedNextSession.repeatNewHifz;
              }
            } else {
              // Sinon, récupérer depuis l'API
              const nextSessionResponse = await onGetNextSessionForStudent(studentId);
              if (nextSessionResponse.success && nextSessionResponse.data && nextSessionResponse.data.repeatNewHifz != null) {
                finalRepeatNewHifz = nextSessionResponse.data.repeatNewHifz;
                // Mettre en cache
                nextSessionCacheRef.current.set(studentId, nextSessionResponse.data);
              }
            }
          }
        } catch (error) {
          console.warn('⚠️ Erreur lors de la récupération de repeatNewHifz depuis quran_next_session:', error);
          // En cas d'erreur, utiliser la valeur de l'état local comme fallback
          finalRepeatNewHifz = repeatNewHifz || 0;
        }
      } else {
        // Si pas de studentId ou callback, utiliser la valeur de l'état local
        finalRepeatNewHifz = repeatNewHifz || 0;
      }
      
      // ✅ Si on crée un nouveau record (pas en mode édition), mettre à jour l'avant-dernier record avec repeatNewHifz
      // Utiliser la route dédiée PATCH /:id/repeat-new-hifz au lieu de PUT complet
      // Mettre à jour même si finalRepeatNewHifz est 0 (valeur valide)
      if (!editingRecordId && newQuranRecord.studentId && onUpdateRepeatNewHifz && finalRepeatNewHifz !== undefined && finalRepeatNewHifz !== null) {
        try {
          const studentId = parseInt(newQuranRecord.studentId);
          if (studentId > 0) {
            // Trouver le dernier record de cet étudiant (qui était "آخر جلسة" avant qu'on crée le nouveau)
            const studentRecords = quranRecords.filter(r => 
              (r.studentId === studentId) ||
              (r.studentName === students.find(s => s.id === studentId)?.studentName)
            );
            
            if (studentRecords.length > 0) {
              // Trier par date décroissante pour obtenir le dernier record
              const sortedRecords = studentRecords.sort((a, b) => {
                const dateA = new Date(a.modifiedDate || a.createdDate || 0).getTime();
                const dateB = new Date(b.modifiedDate || b.createdDate || 0).getTime();
                return dateB - dateA;
              });
              
              const lastRecord = sortedRecords[0];
              if (lastRecord && lastRecord.id) {
                // Utiliser la route dédiée PATCH /:id/repeat-new-hifz pour mettre à jour uniquement repeatNewHifz
                const updateResponse = await onUpdateRepeatNewHifz(lastRecord.id, finalRepeatNewHifz);
                if (updateResponse.success) {
                  console.log('✅ QuranTrackingSection - Avant-dernier record mis à jour avec repeatNewHifz:', finalRepeatNewHifz);
                } else {
                  console.warn('⚠️ QuranTrackingSection - Erreur lors de la mise à jour de l\'avant-dernier record:', updateResponse.error);
                }
              }
            }
          }
        } catch (error) {
          console.warn('⚠️ Erreur lors de la mise à jour de l\'avant-dernier record:', error);
          // Ne pas faire échouer toute l'opération
        }
      }
      
      // 1. Préparer les données pour quran_records (sans les champs nextHalaqa* et repeatNewHifz)
      // Le nouveau record n'a PAS besoin de repeatNewHifz car il sera mis à jour lors de la prochaine création
      const recordToSend = {
        ...newQuranRecord,
        totalHizb: totalHizbValue.toString(),
        // آخر جلسة (Last Session) - UNIQUEMENT ces champs pour quran_records
        newHifzTxt: newMemText,
        oldHifzMurajahTxt: reviewText,
        newHifzMurajahText: newHifzMurajahText,
        note: userNoteText, // ✅ Sauvegarder UNIQUEMENT le texte du professeur (sans évaluations automatiques)
        // Ranges - آخر جلسة
        newHifzRange: newMemPos,
        oldHifzMurajahRange: reviewPos,
        newHifzMurajahRange: newHifzMurajahPos,
        startPoint: newQuranRecord.startPoint || 'الناس',
        // Évaluations (OPTIONNELLES selon l'API - ne pas envoyer si null/undefined)
        ...(validatedEvalNewHifz && { evalNewHifz: validatedEvalNewHifz }),
        ...(validatedEvalOldHifzMurajah && { evalOldHifzMurajah: validatedEvalOldHifzMurajah }),
        ...(validatedEvalNewHifzMurajah && { evalNewHifzMurajah: validatedEvalNewHifzMurajah })
        // ❌ NE PAS INCLURE repeatNewHifz dans le nouveau record (il sera mis à jour lors de la prochaine création)
        // ❌ NE PAS INCLURE : nextHalaqa* (gérés séparément dans quran_next_session)
      };
      
      // 2. Préparer les données pour quran_next_session (si des données sont fournies)
      // ✅ Utiliser les valeurs déjà calculées (nextHalaqaOldMurajahText et nextHalaqaNewMurajahText)
      // qui sont basées sur les sélections ou newQuranRecord.nextHalaqa*Text
      const assignmentText = assignmentSelection.startSurah > 0
        ? formatQuranSelectionLocal(assignmentSelection, 'assignment')
        : (newQuranRecord.nextHalaqaNewHifzText && newQuranRecord.nextHalaqaNewHifzText.trim()
          ? newQuranRecord.nextHalaqaNewHifzText.trim()
          : '');
      
      // Utiliser les valeurs déjà calculées (lignes 2397-2402)
      const nextHalaqaOldMurajahTextFinal = nextHalaqaOldMurajahText || '';
      const nextHalaqaNewMurajahTextFinal = nextHalaqaNewMurajahText || '';
      
      const hasNextSessionData = assignmentText || nextHalaqaOldMurajahTextFinal || nextHalaqaNewMurajahTextFinal || 
        assignmentPos || nextHalaqaOldMurajahPos || nextHalaqaNewMurajahPos || finalRepeatNewHifz > 0;
      
      // Pour quran_next_session, utiliser la valeur de l'état (potentiellement modifiée par l'utilisateur)
      const nextSessionRepeatNewHifz = repeatNewHifz || 0;
      
      const nextSessionData = hasNextSessionData ? {
        nextHalaqaNewHifzText: assignmentText || null,
        nextHalaqaOldHifzMurajahText: nextHalaqaOldMurajahTextFinal || null,
        nextHalaqaNewHifzMurajahText: nextHalaqaNewMurajahTextFinal || null,
        nextHalaqaNewHifzRange: assignmentPos || null,
        nextHalaqaOldHifzMurajahRange: nextHalaqaOldMurajahPos || null,
        nextHalaqaNewHifzMurajahRange: nextHalaqaNewMurajahPos || null,
        repeatNewHifz: nextSessionRepeatNewHifz
      } : null;
      
      // 3. Créer/mettre à jour quran_record
      let response;
      if (editingRecordId) {
        response = await onUpdateQuranRecord(editingRecordId, recordToSend);
      } else {
        response = await onAddQuranRecord(recordToSend);
      }
      
      if (response.success) {
        // 4. Créer/mettre à jour quran_next_session séparément (si des données sont fournies)
        const studentId = parseInt(newQuranRecord.studentId) || 0;
        if (hasNextSessionData && studentId > 0 && onCreateOrUpdateNextSession) {
          try {
            const nextSessionResponse = await onCreateOrUpdateNextSession(studentId, nextSessionData);
            if (!nextSessionResponse.success) {
              console.warn('⚠️ QuranTrackingSection - Erreur lors de la création/mise à jour de la session suivante:', nextSessionResponse.error);
              // Ne pas faire échouer toute l'opération si la session suivante ne peut pas être créée
              // L'utilisateur peut toujours la créer manuellement plus tard
            } else {
              console.log('✅ QuranTrackingSection - Session suivante créée/mise à jour avec succès');
            }
          } catch (nextSessionError) {
            console.error('❌ QuranTrackingSection - Erreur lors de la création/mise à jour de la session suivante:', nextSessionError);
            // Ne pas faire échouer toute l'opération
          }
        }
        
        closeAddQuranRecordModal();
        
        // Vider tous les caches AVANT le refresh pour forcer la mise à jour de "آخر جلسة"
        console.log('🔄 QuranTrackingSection - Vidage des caches après modification');
        latestRecordCacheRef.current.clear();
        nextSessionCacheRef.current.clear(); // ✅ Nettoyer aussi le cache de quran_next_session
        setNextSessionDataLoaded(new Map());
        quranRecordsRef.current = [];
        studentsRef.current = [];
        
        // Appeler les callbacks - ils gèrent déjà le refresh
        // onRecordAdded et onRecordUpdated appellent déjà refreshQuranRecords dans le parent
        if (onRecordAdded && !editingRecordId) {
          await onRecordAdded();
        } else if (onRecordUpdated && editingRecordId) {
          await onRecordUpdated();
        } else {
          // Si aucun callback n'est défini, faire le refresh manuellement
          if (onRefreshQuranRecords) {
            console.log('🔄 QuranTrackingSection - Rechargement immédiat après modification (pas de callback)');
            await onRefreshQuranRecords();
          }
          if (onRefreshStats) {
            await onRefreshStats();
          }
        }
        
        // Forcer la mise à jour de la ref après le refresh pour que le useMemo se recalcule
        // avec les nouvelles données (quranRecords est mis à jour via les props)
        // Le useMemo dépend de quranRecordsIds qui se recalcule quand quranRecords change
      } else {
        const errorMsg = response.error || (editingRecordId ? t('errorUpdatingRecord') || 'خطأ في تحديث السجل' : t('errorAddingRecord'));
        alert(errorMsg);
      }
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : t('errorAddingRecord');
      alert(errorMsg);
      console.error('Erreur lors de l\'ajout/modification de l\'enregistrement Coran:', error);
    }
  }, [newQuranRecord, editingRecordId, quranRecords, students, newMemorizationSelection, reviewSelection, newHifzMurajahSelection, assignmentSelection, nextHalaqaOldMurajahSelection, nextHalaqaNewMurajahSelection, newMemorizationEvaluation, reviewEvaluation, newHifzMurajahEvaluation, evalNewHifz, evalOldHifzMurajah, evalNewHifzMurajah, repeatNewHifz, validateQuranSelection, formatQuranSelectionLocal, verseCounts, loadVerseCount, extractOriginalNoteText, wrapEvaluationText, mapEnglishToArabicEval, onAddQuranRecord, onUpdateQuranRecord, onUpdateRepeatNewHifz, onCreateOrUpdateNextSession, onRecordAdded, onRecordUpdated, onRefreshQuranRecords, onRefreshStats, onGetStudents, surahs.length, loadSurahs, modalStudents.length, t, closeAddQuranRecordModal, calculateHizbFromSelection, onGetNextSessionForStudent]);

  return (
    <div style={{ width: '100%', overflow: 'hidden', boxSizing: 'border-box', maxWidth: '100%' }}>
      {!hideHeader && (
        <div style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between',
          alignItems: isMobile ? 'stretch' : 'center',
          marginBottom: isMobile ? '16px' : '20px',
          flexWrap: 'wrap',
          gap: isMobile ? '12px' : '12px'
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: isMobile ? '8px' : '12px', 
            flexWrap: 'wrap', 
            flex: 1,
            width: isMobile ? '100%' : 'auto'
          }}>
            {!hideTitle && (
              <h1 style={{ 
                fontSize: isMobile ? '18px' : windowWidth < 1024 ? '22px' : '24px',
                fontWeight: '600',
                color: '#424242',
                margin: 0,
                whiteSpace: isMobile ? 'normal' : 'nowrap',
                width: isMobile ? '100%' : 'auto',
                marginBottom: isMobile ? '8px' : '0'
              }}>
                {t('quranTrackingTitle')}
              </h1>
            )}
            {/* Filtre par professeur (si fourni) */}
            {teachers && teachers.length > 0 && (
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: isMobile ? '6px' : '8px',
                width: isMobile ? '100%' : 'auto',
                flex: isMobile ? '1 1 100%' : '0 0 auto'
              }}>
                <span style={{
                  fontSize: isMobile ? '12px' : '14px',
                  color: '#374151',
                  fontWeight: '500',
                  whiteSpace: 'nowrap',
                  direction: isRTL ? 'rtl' : 'ltr',
                  flexShrink: 0
                }}>
                  {t('teacher')}:
                </span>
              <select
                value={currentTeacherFilter}
                onChange={(e) => handleTeacherFilterChange(e.target.value)}
                style={{
                  padding: isMobile ? '8px 12px' : '10px 16px',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  backgroundColor: 'white',
                  fontSize: isMobile ? '13px' : '14px',
                  fontWeight: '500',
                  color: '#374151',
                  cursor: 'pointer',
                  minWidth: isMobile ? '0' : '160px',
                  width: isMobile ? '100%' : 'auto',
                  flex: isMobile ? '1' : '0 0 auto',
                  height: 'fit-content'
                }}
              >
                <option value="all">{t('allTeachersOption')}</option>
                {teachers.filter(teacher => teacher.status === 'active' || (teacher.status as string) === 'نشط').map(teacher => {
                  const teacherUserId = (teacher as any).user_id || teacher.id;
                  return (
                    <option key={teacher.id} value={teacherUserId.toString()}>
                      {teacher.full_name || teacher.username || `${t('teacher')} ${teacher.id}`}
                    </option>
                  );
                })}
              </select>
              </div>
            )}
            {/* Filtre par étudiant */}
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: isMobile ? '6px' : '8px',
              width: isMobile ? '100%' : 'auto',
              flex: isMobile ? '1 1 100%' : '0 0 auto'
            }}>
              <span style={{
                fontSize: isMobile ? '12px' : '14px',
                color: '#374151',
                fontWeight: '500',
                whiteSpace: 'nowrap',
                direction: isRTL ? 'rtl' : 'ltr',
                flexShrink: 0
              }}>
                {t('studentLabel')}:
              </span>
              <div style={{ 
                position: 'relative', 
                minWidth: isMobile ? '0' : '200px',
                width: isMobile ? '100%' : 'auto',
                flex: isMobile ? '1' : '0 0 auto'
              }}>
                <input
                  type="text"
                  value={studentSearchText}
                  onChange={(e) => {
                    const searchText = e.target.value;
                    setStudentSearchText(searchText);
                    setShowStudentDropdown(true);
                    
                    // Si le champ est vide, sélectionner "all"
                    if (searchText === '') {
                      handleStudentFilterChange('all');
                    }
                  }}
                  onFocus={() => {
                    setShowStudentDropdown(true);
                    // Si un étudiant est sélectionné, afficher son nom dans le champ de recherche
                    if (currentStudentFilter !== 'all') {
                      const selectedStudent = students.find(s => s.id.toString() === currentStudentFilter);
                      if (selectedStudent) {
                        setStudentSearchText(selectedStudent.studentName || selectedStudent.student_name || '');
                      }
                    }
                  }}
                  onBlur={() => {
                    // Délai pour permettre le clic sur un élément de la liste
                    setTimeout(() => {
                      setShowStudentDropdown(false);
                      // Si un étudiant est sélectionné, afficher son nom dans le champ
                      if (currentStudentFilter !== 'all') {
                        const selectedStudent = students.find(s => s.id.toString() === currentStudentFilter);
                        if (selectedStudent) {
                          setStudentSearchText(selectedStudent.studentName || selectedStudent.student_name || '');
                        } else {
                          setStudentSearchText('');
                        }
                      } else {
                        setStudentSearchText('');
                      }
                    }, 200);
                  }}
                  placeholder={t('allStudents') || 'جميع الطلاب'}
              style={{
                padding: isMobile ? '8px 12px' : '10px 16px',
                borderRadius: '8px',
                border: '1px solid #e5e7eb',
                backgroundColor: 'white',
                fontSize: isMobile ? '13px' : '14px',
                fontWeight: '500',
                color: '#374151',
                    width: '100%',
                    direction: isRTL ? 'rtl' : 'ltr'
                  }}
                />
                {showStudentDropdown && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    backgroundColor: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    marginTop: '4px',
                    maxHeight: '200px',
                    overflowY: 'auto',
                    zIndex: 1000,
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                  }}>
                    <div
                      onMouseDown={(e) => {
                        e.preventDefault(); // Empêcher le onBlur de fermer le dropdown
                      }}
                      onClick={() => {
                        handleStudentFilterChange('all');
                        setStudentSearchText('');
                        setShowStudentDropdown(false);
                      }}
                      style={{
                        padding: '10px 16px',
                cursor: 'pointer',
                        backgroundColor: currentStudentFilter === 'all' ? '#f3f4f6' : 'white',
                        direction: isRTL ? 'rtl' : 'ltr',
                        fontSize: '14px',
                        borderBottom: '1px solid #e5e7eb'
                      }}
                      onMouseEnter={(e) => {
                        if (currentStudentFilter !== 'all') {
                          e.currentTarget.style.backgroundColor = '#f9fafb';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (currentStudentFilter !== 'all') {
                          e.currentTarget.style.backgroundColor = 'white';
                        }
                      }}
                    >
                      {t('allStudents')}
                    </div>
                    {students
                      .filter(student => {
                        if (!studentSearchText) return true;
                        const name = (student.studentName || student.student_name || '').toLowerCase();
                        return name.includes(studentSearchText.toLowerCase());
                      })
                      .map(student => (
                        <div
                          key={student.id}
                          onMouseDown={(e) => {
                            e.preventDefault(); // Empêcher le onBlur de fermer le dropdown
                          }}
                          onClick={() => {
                            const studentName = student.studentName || student.student_name || '';
                            handleStudentFilterChange(student.id.toString());
                            setStudentSearchText(studentName);
                            setShowStudentDropdown(false);
                          }}
                          style={{
                            padding: '10px 16px',
                            cursor: 'pointer',
                            backgroundColor: currentStudentFilter === student.id.toString() ? '#f3f4f6' : 'white',
                            direction: isRTL ? 'rtl' : 'ltr',
                            fontSize: '14px'
                          }}
                          onMouseEnter={(e) => {
                            if (currentStudentFilter !== student.id.toString()) {
                              e.currentTarget.style.backgroundColor = '#f9fafb';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (currentStudentFilter !== student.id.toString()) {
                              e.currentTarget.style.backgroundColor = 'white';
                            }
                          }}
                        >
                  {student.studentName || student.student_name}
                        </div>
              ))}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div style={{ 
            display: 'flex', 
            gap: isMobile ? '8px' : '12px', 
            alignItems: 'center', 
            flexWrap: 'wrap',
            width: isMobile ? '100%' : 'auto',
            justifyContent: isMobile ? 'stretch' : 'flex-end'
          }}>
            {/* Combobox de tri - Masqué mais fonctionnel */}
            {/* <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: isMobile ? '6px' : '8px',
              flex: isMobile ? '1' : '0 0 auto'
            }}>
              <label style={{
                fontSize: isMobile ? '12px' : '13px',
                fontWeight: '500',
                color: '#374151',
                whiteSpace: 'nowrap'
              }}>
                ترتيب:
              </label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as 'createdDate' | 'alphabetical')}
                style={{
                  padding: isMobile ? '8px 12px' : '10px 16px',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  backgroundColor: 'white',
                  fontSize: isMobile ? '13px' : '14px',
                  fontWeight: '500',
                  color: '#374151',
                  cursor: 'pointer',
                  direction: isRTL ? 'rtl' : 'ltr',
                  minWidth: isMobile ? '140px' : '170px'
                }}
              >
                <option value="createdDate">{qt.sortByLastSession}</option>
                <option value="alphabetical">ترتيب أبجدي</option>
              </select>
            </div> */}
            <button
              onClick={handleRefresh}
              disabled={refreshing === 'quran' || refreshing === 'stats'}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: isMobile ? '6px' : '8px',
                padding: isMobile ? '8px 12px' : '10px 16px',
                backgroundColor: (refreshing === 'quran' || refreshing === 'stats') ? '#9ca3af' : '#9333ea',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: (refreshing === 'quran' || refreshing === 'stats') ? 'not-allowed' : 'pointer',
                fontSize: isMobile ? '13px' : '14px',
                fontWeight: '500',
                transition: 'all 0.2s ease',
                whiteSpace: 'nowrap',
                flex: isMobile ? '1' : '0 0 auto',
                minWidth: isMobile ? '0' : 'auto'
              }}
            >
              <RefreshIcon animate={refreshing === 'quran' || refreshing === 'stats'} size={isMobile ? 14 : 16} />
              <span>{t('update')}</span>
            </button>
            {canModify && (
              <button 
                onClick={() => openQuranRecordModal()}
                style={{
                  ...finalStyles.button,
                  ...finalStyles.primaryButton,
                  whiteSpace: 'nowrap',
                  padding: isMobile ? '8px 12px' : finalStyles.button.padding,
                  fontSize: isMobile ? '13px' : finalStyles.button.fontSize,
                  flex: isMobile ? '1' : '0 0 auto',
                  minWidth: isMobile ? '0' : 'auto'
                }}
              >
                {t('addNewRecord')}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Student Progress Cards */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        {useMemo(() => {
          // Utiliser directement students (la prop) pour s'assurer que les données sont toujours à jour
          // studentsRef.current est mis à jour dans handleRefresh, mais pour éviter les problèmes de synchronisation,
          // on utilise directement students qui est mis à jour par le parent
          
          // ✅ Récupérer les données de quran_next_session pour tous les étudiants visibles
          // (fait de manière asynchrone dans un useEffect séparé pour ne pas bloquer le rendu)
          const studentsToUse = students.length > 0 ? students : studentsRef.current;
          let filteredStudents = currentStudentFilter === 'all' 
            ? studentsToUse 
            : studentsToUse.filter(s => s.id.toString() === currentStudentFilter);
          
          // Trier les étudiants selon le critère sélectionné
          if (sortBy === 'alphabetical') {
            filteredStudents = [...filteredStudents].sort((a, b) => {
              const nameA = (a.studentName || a.student_name || '').toLowerCase();
              const nameB = (b.studentName || b.student_name || '').toLowerCase();
              return nameA.localeCompare(nameB, 'ar');
            });
          } else {
            // Tri par date de création آخر جلسة (par défaut) - trier par la date de création du dernier enregistrement
            filteredStudents = [...filteredStudents].sort((a, b) => {
              const recordsA = quranRecords.filter(r => 
                (r.studentId === a.id) || (r.studentName === a.studentName) || (r.studentName === a.student_name)
              );
              const recordsB = quranRecords.filter(r => 
                (r.studentId === b.id) || (r.studentName === b.studentName) || (r.studentName === b.student_name)
              );
              
              // Trier les enregistrements par date de modification pour trouver le dernier, puis utiliser sa date de création
              const latestA = recordsA.length > 0 ? recordsA.sort((r1, r2) => {
                const date1 = new Date(r1.modifiedDate || r1.createdDate || 0).getTime();
                const date2 = new Date(r2.modifiedDate || r2.createdDate || 0).getTime();
                return date2 - date1;
              })[0] : null;
              
              const latestB = recordsB.length > 0 ? recordsB.sort((r1, r2) => {
                const date1 = new Date(r1.modifiedDate || r1.createdDate || 0).getTime();
                const date2 = new Date(r2.modifiedDate || r2.createdDate || 0).getTime();
                return date2 - date1;
              })[0] : null;
              
              if (!latestA && !latestB) return 0;
              if (!latestA) return 1;
              if (!latestB) return -1;
              
              // Utiliser la date de création (createdDate) du dernier enregistrement pour le tri
              const dateA = new Date(latestA.createdDate || 0).getTime();
              const dateB = new Date(latestB.createdDate || 0).getTime();
              return dateB - dateA; // Plus récent en premier
            });
          }
          
          if (filteredStudents.length === 0) {
            return (
              <div key="no-students" style={{
                ...finalStylesRef.current.card,
                textAlign: 'center',
                padding: '48px',
                color: '#6b7280'
              }}>
                <p style={{ fontSize: '1.125rem' }}>
                  {(students.length === 0 && studentsRef.current.length === 0) ? tRef.current('noStudents') : tRef.current('noStudentsFilter')}
                </p>
              </div>
            );
          }
          
          // ✅ NOUVELLE LOGIQUE SIMPLE - Utilise openQuranRecordModal
          const createHandleEditRecord = (student: any, latestRecord: any) => {
            return async () => {
              console.log('🔍 handleEditRecord - Édition simple:', latestRecord);
              
              // Ouvrir le modal avec les données du record
              await openQuranRecordModal(latestRecord);
            };
          };
          
          return filteredStudents.map((student) => {
            // Utiliser directement quranRecords (qui est dans les dépendances via quranRecordsIds)
            // pour s'assurer que les données sont toujours à jour après un refresh
            const studentRecords = quranRecords.filter(r => {
              // Vérifier que l'enregistrement correspond à l'étudiant
              const matchesStudent = (r.studentId === student.id) ||
                (r.studentName === student.studentName) ||
                (r.studentName === student.student_name);
              
              // Si un filtre teacher est actif, vérifier aussi le teacherId
              if (currentTeacherFilter !== 'all' && currentTeacherFilter !== undefined) {
                const teacherId = parseInt(currentTeacherFilter);
                return matchesStudent && (r.teacherId === teacherId);
              }
              
              return matchesStudent;
            });
            
            // Log pour vérifier les valeurs dans studentRecords
            /*if (studentRecords.length > 0 && (studentRecords[0].evalNewHifz === 'Absent' || studentRecords[0].evalOldHifzMurajah === 'Absent' || !studentRecords[0].evalNewHifz || !studentRecords[0].evalOldHifzMurajah)) {
              console.log('🔍 QuranTrackingSection - studentRecords filtrés:', {
                student_id: student.id,
                student_name: student.student_name,
                studentRecords_count: studentRecords.length,
                first_record: {
                  id: studentRecords[0].id,
                  studentId: studentRecords[0].studentId,
                  evalNewHifz: studentRecords[0].evalNewHifz,
                  evalOldHifzMurajah: studentRecords[0].evalOldHifzMurajah,
                  evalNewHifz_type: typeof studentRecords[0].evalNewHifz,
                  evalOldHifzMurajah_type: typeof studentRecords[0].evalOldHifzMurajah,
                  record_keys: Object.keys(studentRecords[0])
                },
                quranRecords_sample: quranRecords.length > 0 ? {
                  id: quranRecords[0].id,
                  evalNewHifz: quranRecords[0].evalNewHifz,
                  evalOldHifzMurajah: quranRecords[0].evalOldHifzMurajah
                } : null
              });
            }*/
            // Trier les enregistrements (créer une copie pour éviter de muter l'original)
            const sortedRecords = [...studentRecords].sort((a, b) => {
              const dateA = new Date(a.modifiedDate || a.createdDate || 0).getTime();
              const dateB = new Date(b.modifiedDate || b.createdDate || 0).getTime();
              if (dateB !== dateA) return dateB - dateA;
              const createdA = a.createdDate ? new Date(a.createdDate).getTime() : 0;
              const createdB = b.createdDate ? new Date(b.createdDate).getTime() : 0;
              return createdB - createdA;
            });
            // Utiliser le cache pour éviter de créer de nouveaux objets à chaque rendu
            const rawLatestRecord = sortedRecords[0];
            let latestRecord = rawLatestRecord;
            if (rawLatestRecord) {
              const cacheKey = rawLatestRecord.id || student.id;
              const cached = latestRecordCacheRef.current.get(cacheKey);
              // Vérifier si l'enregistrement en cache est toujours valide (même ID et mêmes propriétés clés)
              // Comparer aussi les autres champs importants pour détecter les modifications
              // IMPORTANT: Inclure total_hizb et totalHizb dans la comparaison pour détecter les changements de progression
              // Convertir en nombres pour une comparaison correcte
              const getNumericTotalHizb = (record: any): number => {
                const value = record?.total_hizb ?? record?.totalHizb ?? 0;
                if (typeof value === 'string') {
                  const parsed = parseFloat(value);
                  return !isNaN(parsed) ? parsed : 0;
                }
                return typeof value === 'number' ? value : 0;
              };
              const cachedTotalHizb = getNumericTotalHizb(cached);
              const rawTotalHizb = getNumericTotalHizb(rawLatestRecord);
              
              if (cached && cached.id === rawLatestRecord.id && 
                  cached.modifiedDate === rawLatestRecord.modifiedDate &&
                  cached.evalNewHifz === rawLatestRecord.evalNewHifz &&
                  cached.evalOldHifzMurajah === rawLatestRecord.evalOldHifzMurajah &&
                  cached.newHifzTxt === rawLatestRecord.newHifzTxt &&
                  cached.oldHifzMurajahTxt === rawLatestRecord.oldHifzMurajahTxt &&
                  cached.nextHalaqaNewHifzText === rawLatestRecord.nextHalaqaNewHifzText &&
                  cached.note === rawLatestRecord.note &&
                  cachedTotalHizb === rawTotalHizb) {
                latestRecord = cached;
              } else {
                // Mettre à jour le cache avec le nouvel enregistrement
                // S'assurer que totalHizb est un nombre dans le cache
                const recordToCache = {
                  ...rawLatestRecord,
                  totalHizb: rawTotalHizb
                };
                latestRecordCacheRef.current.set(cacheKey, recordToCache);
                latestRecord = recordToCache;
              }
            }
            
            // Log pour vérifier les valeurs dans sortedRecords
            /*
            if (latestRecord && (latestRecord.evalNewHifz === 'Absent' || latestRecord.evalOldHifzMurajah === 'Absent' || !latestRecord.evalNewHifz || !latestRecord.evalOldHifzMurajah)) {
              console.log('🔍 QuranTrackingSection - latestRecord depuis sortedRecords:', {
                id: latestRecord.id,
                studentId: latestRecord.studentId,
                evalNewHifz: latestRecord.evalNewHifz,
                evalOldHifzMurajah: latestRecord.evalOldHifzMurajah,
                evalNewHifz_type: typeof latestRecord.evalNewHifz,
                evalOldHifzMurajah_type: typeof latestRecord.evalOldHifzMurajah,
                latestRecord_keys: Object.keys(latestRecord),
                studentRecords_count: studentRecords.length,
                sortedRecords_count: sortedRecords.length,
                first_studentRecord: studentRecords[0] ? {
                  id: studentRecords[0].id,
                  evalNewHifz: studentRecords[0].evalNewHifz,
                  evalOldHifzMurajah: studentRecords[0].evalOldHifzMurajah
                } : null
              });
            }
            */
            let totalHizb = 0;
            if (latestRecord) {
              const hizbValue = latestRecord.totalHizb;
              if (typeof hizbValue === 'string') {
                const parsed = parseFloat(hizbValue);
                totalHizb = !isNaN(parsed) ? parsed : 0;
              } else if (typeof hizbValue === 'number') {
                totalHizb = hizbValue;
              }
              
            }

            let progressColor = '#ff9800';
            if (totalHizb >= 20) {
              progressColor = '#4caf50';
            } else if (totalHizb >= 15) {
              progressColor = '#2196f3';
            } else if (totalHizb >= 5) {
              progressColor = '#ffc107';
            }

            const progressPercentage = totalHizb > 0 ? Math.min((totalHizb / 60) * 100, 100) : 0;

            return (
              <div
                key={student.id}
                style={{
                  ...finalStylesRef.current.card,
                  borderLeft: isRTL ? undefined : `4px solid ${progressColor}`,
                  borderRight: isRTL ? `4px solid ${progressColor}` : undefined,
                  padding: isMobile ? '16px' : '24px'
                }}
              >
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: isMobile ? '12px' : '16px',
                  gap: isMobile ? '12px' : '16px',
                  flexWrap: isMobile ? 'wrap' : 'nowrap'
                }}>
                  <h3 style={{
                    fontSize: isMobile ? '1rem' : '1.125rem',
                    fontWeight: 'bold',
                    color: '#1f2937',
                    margin: 0,
                    flex: 1
                  }}>
                    {student.studentName || student.student_name}
                  </h3>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: isMobile ? '6px' : '8px',
                    flexShrink: 0
                  }}>
                    <div style={{
                      fontSize: isMobile ? '1.25rem' : '1.5rem',
                      fontWeight: 'bold',
                      color: '#4caf50',
                      lineHeight: '1.2'
                    }}>
                      {totalHizb > 0 ? totalHizb.toFixed(1) : '0'}
                    </div>
                    <div style={{ 
                      fontSize: isMobile ? '12px' : '13px', 
                      color: '#6b7280',
                      lineHeight: '1.2'
                    }}>
                      {tRef.current('quranHizbLabel')}
                    </div>
                  </div>
                </div>

                {/* Progress Bar */}
                <div style={{ marginBottom: isMobile ? '12px' : '16px' }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '6px'
                  }}>
                    <span style={{ fontSize: isMobile ? '12px' : '13px', fontWeight: '500', color: '#374151' }}>
                      {tRef.current('quranProgressLabel')}
                    </span>
                    <span style={{ fontSize: isMobile ? '12px' : '13px', color: '#6b7280' }}>
                      {Math.round(progressPercentage)}{tRef.current('quranProgressPercent')}
                    </span>
                  </div>
                  <div style={{
                    width: '100%',
                    height: '12px',
                    backgroundColor: '#e5e7eb',
                    borderRadius: '9999px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      width: `${progressPercentage}%`,
                      height: '100%',
                      background: `linear-gradient(to right, ${progressColor}, ${progressColor}dd)`,
                      transition: 'width 0.5s ease'
                    }} />
                  </div>
                </div>

                {/* Latest Record */}
                {latestRecord && (() => {
                  const recordDate = new Date(latestRecord.modifiedDate || latestRecord.createdDate || '');
                  const today = new Date();
                  const isToday = recordDate.toDateString() === today.toDateString();
                  
                  const handleEditRecord = createHandleEditRecord(student, latestRecord);
                  
                  // ✅ NOUVEAU - Fonction pour mettre à jour les évaluations
                  const handleUpdateEvaluations = async (recordId: number, evaluations: { evalNewHifz?: string; evalOldHifzMurajah?: string; evalNewHifzMurajah?: string }) => {
                    if (!onUpdateQuranRecord) {
                      console.error('❌ QuranTrackingSection - onUpdateQuranRecord non défini');
                      return;
                    }

                    try {
                      // Récupérer le studentId du record
                      const record = quranRecords.find(r => r.id === recordId);
                      if (!record) {
                        console.error('❌ QuranTrackingSection - Record non trouvé pour ID:', recordId);
                        return;
                      }

                      // Préparer les données avec studentId et les évaluations
                      const recordData = {
                        studentId: record.studentId,
                        ...evaluations
                      };

                      // Appeler onUpdateQuranRecord avec studentId et les évaluations
                      const response = await onUpdateQuranRecord(recordId, recordData);
                      
                      if (response.success) {
                        // Rafraîchir les données après la mise à jour
                        if (onRefreshQuranRecords) {
                          await onRefreshQuranRecords();
                        }
                        if (onRecordUpdated) {
                          onRecordUpdated();
                        }
                        console.log('✅ QuranTrackingSection - Évaluations mises à jour avec succès');
                      } else {
                        throw new Error(response.error || 'Erreur lors de la mise à jour des évaluations');
                      }
                    } catch (error) {
                      console.error('❌ QuranTrackingSection - Erreur lors de la mise à jour des évaluations:', error);
                      throw error;
                    }
                  };
                  
                  // Calculer previousRecords pour extraire le dernier enregistrement
                  const previousRecords = latestRecord 
                    ? sortedRecords.filter(record => record.id !== latestRecord.id)
                    : sortedRecords.slice(1);
                  
                  // Extraire le deuxième plus récent enregistrement (le premier dans previousRecords)
                  // previousRecords est trié du plus récent au plus ancien
                  // previousRecords[0] = deuxième plus récent (premier dans "الجلسات السابقة")
                  const lastPreviousRecord = previousRecords.length > 0 
                    ? previousRecords[0] 
                    : null;
                  
                  // ✅ Récupérer les données de quran_next_session depuis le cache ou l'API
                  const studentId = latestRecord.studentId || student.id;
                  const cachedNextSession = nextSessionCacheRef.current.get(studentId);
                  
                  // ✅ Fusionner latestRecord avec les données de quran_next_session
                  // latestRecord.repeatNewHifz vient de quran_records (pour "آخر جلسة")
                  // Pour "الجلسة القادمة", on doit utiliser nextSession.repeatNewHifz
                  const latestRecordWithNextSession = {
                    ...latestRecord,
                    // ✅ S'assurer que studentId est inclus (nécessaire pour la sauvegarde)
                    studentId: latestRecord.studentId || student.id,
                    // ✅ Données de الجلسة القادمة depuis quran_next_session
                    nextHalaqaNewHifzText: cachedNextSession?.nextHalaqaNewHifzText || latestRecord.nextHalaqaNewHifzText || '',
                    nextHalaqaOldHifzMurajahText: cachedNextSession?.nextHalaqaOldHifzMurajahText || latestRecord.nextHalaqaOldHifzMurajahText || '',
                    nextHalaqaNewHifzMurajahText: cachedNextSession?.nextHalaqaNewHifzMurajahText || latestRecord.nextHalaqaNewHifzMurajahText || '',
                    // ✅ repeatNewHifz pour "الجلسة القادمة" - depuis quran_next_session uniquement
                    // ⚠️ IMPORTANT: latestRecord.repeatNewHifz vient de quran_records (pour "تكرار آخر حفظ")
                    // Pour "تكرار الحفظ الجديد" dans "الجلسة القادمة", on utilise nextSession.repeatNewHifz
                    repeatNewHifz: (cachedNextSession && cachedNextSession.repeatNewHifz != null) 
                      ? cachedNextSession.repeatNewHifz 
                      : latestRecord.repeatNewHifz
                  };
                  
                  // Passer latestRecord avec les données de quran_next_session fusionnées
                  // React.memo dans QuranRecordSection comparera les propriétés individuelles
                  return (
                    <QuranRecordSection
                      latestRecord={latestRecordWithNextSession as any}
                      language={currentLanguage as 'ar' | 'fr' | 'en'}
                      cleanText={true}
                      onEdit={handleEditRecord}
                      canEdit={isToday && canModify}
                      mapEnglishToArabicEval={mapEnglishToArabicEval}
                      extractOriginalNoteText={extractOriginalNoteText}
                      windowWidth={windowWidth}
                      targetRepeatNewHifzCount={targetRepeatNewHifzCount}
                      onUpdateRepeatNewHifz={onUpdateRepeatNewHifzInNextSession}
                      onUpdateEvaluations={isToday && canModify ? handleUpdateEvaluations : undefined}
                      lastPreviousRecord={lastPreviousRecord ? (() => {
                        const repeatValue = lastPreviousRecord.repeatNewHifz != null 
                          ? lastPreviousRecord.repeatNewHifz 
                          : undefined;
                        
                        
                        // Créer un objet avec une référence stable basée sur les valeurs
                        // Utiliser une clé composite pour la stabilité
                        const lastPreviousKey = `${lastPreviousRecord.id}_${repeatValue}_${lastPreviousRecord.newHifzTxt}`;
                        
                        return {
                          newHifzTxt: lastPreviousRecord.newHifzTxt,
                          // ✅ repeatNewHifz doit venir de quran_records uniquement
                          // Si absent ou null, on laisse undefined (pas de fallback)
                          // Note: 0 est considéré comme une valeur valide, seul null/undefined est considéré comme absent
                          repeatNewHifz: repeatValue,
                          // Clé pour la stabilité (ne sera pas utilisée dans le composant, juste pour la comparaison)
                          _key: lastPreviousKey
                        };
                      })() : undefined}
                    />
                  );
                })()}

                {/* Previous Records Table */}
                {(() => {
                  const previousRecords = latestRecord 
                    ? sortedRecords.filter(record => record.id !== latestRecord.id)
                    : sortedRecords.slice(1);
                  
                  if (previousRecords.length === 0) return null;

                  // ✅ Limiter UNIQUEMENT l'affichage : le suivi est longitudinal et peut couvrir plusieurs années
                  // maxPreviousSessions = 0 => tout afficher
                  const totalPreviousRecords = previousRecords.length;
                  const displayedPreviousRecords = maxPreviousSessions > 0
                    ? previousRecords.slice(0, maxPreviousSessions)
                    : previousRecords;

                  const isExpanded = expandedStudentIds.has(student.id);

                  return (
                    <div style={{
                      marginTop: isMobile ? '12px' : '16px',
                      borderTop: '1px solid #e5e7eb',
                    }}>
                      {/* Bouton toggle par carte élève */}
                      <button
                        onClick={() => toggleStudentSessions(student.id)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px',
                          width: '100%',
                          padding: '10px 12px',
                          backgroundColor: 'transparent',
                          color: '#6b7280',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: isMobile ? '13px' : '14px',
                          fontWeight: '600',
                          transition: 'background-color 0.2s ease',
                          borderRadius: '0 0 8px 8px',
                        }}
                        onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#f3f4f6'; }}
                        onMouseOut={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                      >
                        <span style={{ fontSize: '11px', transition: 'transform 0.2s', transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)', display: 'inline-block' }}>▶</span>
                        <span>{tRef.current('previousSessions')} ({displayedPreviousRecords.length}{displayedPreviousRecords.length < totalPreviousRecords ? ` / ${totalPreviousRecords}` : ''})</span>
                      </button>

                      {/* Tableau des séances précédentes */}
                      {isExpanded && (
                    <div style={{
                      marginTop: isMobile ? '0' : '0',
                      backgroundColor: '#f9fafb',
                      borderRadius: '8px',
                      padding: isMobile ? '12px' : '16px',
                      overflow: 'hidden'
                    }}>
                      <div style={{ 
                        overflowX: 'auto',
                        overflowY: 'auto',
                        maxHeight: isMobile ? '300px' : '400px',
                        border: '1px solid #e5e7eb',
                        borderRadius: '6px',
                        WebkitOverflowScrolling: 'touch'
                      }}>
                        <table style={{
                          width: '100%',
                          borderCollapse: 'collapse',
                          fontSize: isMobile ? '12px' : '13px',
                          minWidth: isMobile ? '500px' : '600px'
                        }}>
                          <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                            <tr style={{
                              backgroundColor: '#f3f4f6',
                              borderBottom: '2px solid #e5e7eb'
                            }}>
                              <th style={{
                                padding: isMobile ? '8px 10px' : '10px 12px',
                                textAlign: isRTL ? 'right' : 'left',
                                fontWeight: '600',
                                color: '#374151',
                                fontSize: isMobile ? '11px' : '12px',
                                backgroundColor: '#f3f4f6',
                                whiteSpace: 'nowrap'
                              }}>
                                {tRef.current('quranTableDate')}
                              </th>
                              <th style={{
                                padding: isMobile ? '8px 10px' : '10px 12px',
                                textAlign: isRTL ? 'right' : 'left',
                                fontWeight: '600',
                                color: '#374151',
                                fontSize: isMobile ? '11px' : '12px',
                                backgroundColor: '#f3f4f6',
                                whiteSpace: 'nowrap'
                              }}>
                                {tRef.current('quranTableNewHifz')}
                              </th>
                              <th style={{
                                padding: isMobile ? '8px 10px' : '10px 12px',
                                textAlign: isRTL ? 'right' : 'left',
                                fontWeight: '600',
                                color: '#374151',
                                fontSize: isMobile ? '11px' : '12px',
                                backgroundColor: '#f3f4f6',
                                whiteSpace: 'nowrap'
                              }}>
                                {tRef.current('quranTableOldMurajah')}
                              </th>
                              <th style={{
                                padding: isMobile ? '8px 10px' : '10px 12px',
                                textAlign: isRTL ? 'right' : 'left',
                                fontWeight: '600',
                                color: '#374151',
                                fontSize: isMobile ? '11px' : '12px',
                                backgroundColor: '#f3f4f6',
                                whiteSpace: 'nowrap'
                              }}>
                                {tRef.current('quranTableNewMurajah')}
                              </th>
                              <th style={{
                                padding: isMobile ? '8px 10px' : '10px 12px',
                                textAlign: isRTL ? 'right' : 'left',
                                fontWeight: '600',
                                color: '#374151',
                                fontSize: isMobile ? '11px' : '12px',
                                backgroundColor: '#f3f4f6',
                                whiteSpace: 'nowrap'
                              }}>
                                {tRef.current('quranTableEvalNewHifz')}
                              </th>
                              <th style={{
                                padding: '10px 8px',
                                textAlign: isRTL ? 'right' : 'left',
                                fontWeight: '600',
                                color: '#374151',
                                fontSize: '12px',
                                backgroundColor: '#f3f4f6',
                                whiteSpace: 'normal',
                                lineHeight: '1.3',
                                maxWidth: '80px',
                                minWidth: '70px'
                              }}>
                                {tRef.current('quranTableEvalOldMurajah')}
                              </th>
                              <th style={{
                                padding: '10px 8px',
                                textAlign: isRTL ? 'right' : 'left',
                                fontWeight: '600',
                                color: '#374151',
                                fontSize: '12px',
                                backgroundColor: '#f3f4f6',
                                whiteSpace: 'normal',
                                lineHeight: '1.3',
                                maxWidth: '80px',
                                minWidth: '70px'
                              }}>
                                {tRef.current('quranTableEvalNewMurajah')}
                              </th>
                              <th style={{
                                padding: isMobile ? '8px 10px' : '10px 12px',
                                textAlign: isRTL ? 'right' : 'left',
                                fontWeight: '600',
                                color: '#374151',
                                fontSize: isMobile ? '11px' : '12px',
                                backgroundColor: '#f3f4f6',
                                whiteSpace: 'nowrap'
                              }}>
                                {tRef.current('quranTableNote')}
                              </th>
                              <th style={{
                                padding: isMobile ? '8px 10px' : '10px 12px',
                                textAlign: isRTL ? 'right' : 'left',
                                fontWeight: '600',
                                color: '#374151',
                                fontSize: isMobile ? '11px' : '12px',
                                backgroundColor: '#f3f4f6',
                                whiteSpace: 'nowrap'
                              }}>
                                {tRef.current('quranTableHizb')}
                              </th>
                              <th style={{ display: 'none' }}>
                                repeatNewHifz
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {displayedPreviousRecords.map((record, index) => {
                              const hizbValue = record.totalHizb;
                              let totalHizb = 0;
                              if (typeof hizbValue === 'string') {
                                totalHizb = parseFloat(hizbValue) || 0;
                              } else if (typeof hizbValue === 'number') {
                                totalHizb = hizbValue;
                              }
                              
                              return (
                                <tr
                                  key={record.id || index}
                                  style={{
                                    borderBottom: '1px solid #e5e7eb',
                                    backgroundColor: index % 2 === 0 ? 'white' : '#fafafa'
                                  }}
                                >
                                  <td style={{
                                    padding: isMobile ? '8px 10px' : '10px 12px',
                                    color: '#6b7280',
                                    fontWeight: '500'
                                  }}>
                                    {(() => {
                                      const dateString = record.modifiedDate || record.createdDate || '';
                                      if (!dateString) return '';
                                      
                                      // Utiliser la fonction globale pour obtenir la date et l'heure
                                      const { date: formattedDate, time: formattedTime, isValid } = GLOBAL_UTILS.getDateTimeParts(dateString);
                                      if (!isValid) return '';
                                      
                                      return (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                          <div style={{
                                            fontSize: '13px',
                                            fontWeight: '500',
                                            color: '#374151'
                                          }}>
                                            {formattedDate}
                                          </div>
                                          <div style={{
                                            fontSize: '12px',
                                            fontWeight: '500',
                                            color: '#6b7280',
                                            letterSpacing: '0.5px'
                                          }}>
                                            {formattedTime}
                                          </div>
                                        </div>
                                      );
                                    })()}
                                  </td>
                                  <td style={{
                                    padding: isMobile ? '8px 10px' : '10px 12px',
                                    color: '#374151',
                                    maxWidth: '200px',
                                    wordBreak: 'break-word'
                                  }}>
                                    {GLOBAL_UTILS.cleanText(record.newHifzTxt || '-')}
                                  </td>
                                  <td style={{
                                    padding: isMobile ? '8px 10px' : '10px 12px',
                                    color: '#374151',
                                    maxWidth: '200px',
                                    wordBreak: 'break-word'
                                  }}>
                                    {GLOBAL_UTILS.cleanText(record.oldHifzMurajahTxt || '-')}
                                  </td>
                                  <td style={{
                                    padding: isMobile ? '8px 10px' : '10px 12px',
                                    color: '#374151',
                                    maxWidth: '200px',
                                    wordBreak: 'break-word'
                                  }}>
                                    {GLOBAL_UTILS.cleanText(record.newHifzMurajahText || '-')}
                                  </td>
                                  <td style={{
                                    padding: isMobile ? '8px 10px' : '10px 12px',
                                    color: '#374151',
                                    textAlign: 'center',
                                    fontWeight: '500',
                                    whiteSpace: 'nowrap'
                                  }}>
                                    {mapEvalToDisplay(record.evalNewHifz)}
                                  </td>
                                  <td style={{
                                    padding: isMobile ? '8px 10px' : '10px 12px',
                                    color: '#374151',
                                    textAlign: 'center',
                                    fontWeight: '500',
                                    whiteSpace: 'nowrap'
                                  }}>
                                    {mapEvalToDisplay(record.evalOldHifzMurajah)}
                                  </td>
                                  <td style={{
                                    padding: isMobile ? '8px 10px' : '10px 12px',
                                    color: '#374151',
                                    textAlign: 'center',
                                    fontWeight: '500',
                                    whiteSpace: 'nowrap'
                                  }}>
                                    {mapEvalToDisplay(record.evalNewHifzMurajah)}
                                  </td>
                                  <td style={{
                                    padding: isMobile ? '8px 10px' : '10px 12px',
                                    color: '#374151',
                                    maxWidth: '200px',
                                    wordBreak: 'break-word'
                                  }}>
                                    {record.note ? GLOBAL_UTILS.cleanText(record.note) : ''}
                                  </td>
                                  <td style={{
                                    padding: isMobile ? '8px 10px' : '10px 12px',
                                    color: '#374151',
                                    textAlign: 'center',
                                    fontWeight: '500',
                                    whiteSpace: 'nowrap'
                                  }}>
                                    {totalHizb > 0 ? totalHizb.toFixed(1) : '0'}
                                  </td>
                                  <td style={{ display: 'none' }}>
                                    {record.repeatNewHifz || 0}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                      )}
                    </div>
                  );
                })()}

                {!latestRecord && (
                  <div style={{
                    backgroundColor: '#f9fafb',
                    borderRadius: '8px',
                    padding: '16px',
                    marginTop: '16px',
                    textAlign: 'center',
                    color: '#6b7280'
                  }}>
                    <p style={{ fontSize: '14px', margin: 0 }}>{t('noRecords')}</p>
                  </div>
                )}
              </div>
            );
          });
          // Note: Certaines dépendances sont intentionnellement omises pour éviter les re-renders inutiles.
          // - calculateHizbFromSelection, mapEnglishToArabicEval, onGetQuranRecordById, onRefreshQuranRecords: fonctions stables
          // - modalStudents.length, newQuranRecord.startPoint: utilisés uniquement dans createHandleEditRecord (closure)
          // - students, surahs: utilisés via studentsRef.current et dans les closures, pas directement dans le rendu
          // - quranRecords: utilisé via quranRecordsRef.current pour éviter les re-renders inutiles
          // - t, finalStyles: utilisés via tRef.current et finalStylesRef.current pour éviter les re-renders inutiles
          // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [studentsIds, quranRecordsIds, currentStudentFilter, currentTeacherFilter, quranRecords, students, nextSessionDataLoaded, expandedStudentIds, sortBy])}
      </div>

      {/* ========== MODAL D'AJOUT/ÉDITION DE SUJL QURAN ========== */}
      {showQuranRecordModal && (
        <div style={finalStyles.modal}>
          <div style={{...finalStyles.modalContent, maxHeight: '90vh', overflowY: 'auto', maxWidth: '500px'}}>
            <div style={{ marginBottom: '16px' }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '12px',
                flexWrap: 'wrap',
                direction: isRTL ? 'rtl' : 'ltr'
              }}>
                <h2 style={{ fontSize: '1.1rem', fontWeight: '600', color: '#1f2937', margin: 0 }}>
                  {editingRecordId ? t('editQuranRecord') : t('addQuranRecord')}
                </h2>
                
                {/* ✅ Afficher le nom du professeur comme badge (pour AdminDashboard ET TeacherDashboard) */}
                {(() => {
                  // Pour AdminDashboard : récupérer depuis teachers + currentTeacherFilter
                  if (teachers && teachers.length > 0 && currentTeacherFilter && currentTeacherFilter !== 'all') {
                    const selectedTeacher = teachers.find(t => t.id.toString() === currentTeacherFilter || (t as any).user_id?.toString() === currentTeacherFilter);
                    const teacherName = selectedTeacher ? (selectedTeacher.full_name || selectedTeacher.username || `أستاذ ${selectedTeacher.id}`) : '';
                    
                    if (teacherName) {
                      return (
                        <span style={{
                          padding: '4px 10px',
                          backgroundColor: '#f0f9ff',
                          borderRadius: '6px',
                          border: '1px solid #0ea5e9',
                          fontSize: '13px',
                          fontWeight: '500',
                          color: '#0369a1',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          whiteSpace: 'nowrap'
                        }}>
                          <span style={{ fontWeight: '600' }}>الأستاذ:</span>
                          <span>{teacherName}</span>
                        </span>
                      );
                    }
                  }
                  
                  // Pour TeacherDashboard : utiliser currentTeacherName
                  if (currentTeacherName) {
                    return (
                      <span style={{
                        padding: '4px 10px',
                        backgroundColor: '#f0f9ff',
                        borderRadius: '6px',
                        border: '1px solid #0ea5e9',
                        fontSize: '13px',
                        fontWeight: '500',
                        color: '#0369a1',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        whiteSpace: 'nowrap'
                      }}>
                        <span style={{ fontWeight: '600' }}>الأستاذ:</span>
                        <span>{currentTeacherName}</span>
                      </span>
                    );
                  }
                  
                  return null;
                })()}
              </div>
            </div>
            
            <form onSubmit={handleAddQuranRecord} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* Ligne unique : الطالب (2x) + بداية الحفظ + الأحزاب */}
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '12px' }}>
                {/* الطالب (plus large) */}
                <div style={finalStyles.formGroup}>
                  <label style={{...finalStyles.label, fontSize: '14px', marginBottom: '6px'}}>{t('student')}</label>
                  {loadingModalStudents ? (
                    <div style={{ padding: '12px', textAlign: 'center', color: '#6b7280', direction: isRTL ? 'rtl' : 'ltr' }}>
                      {t('loadingStudents')}
                    </div>
                  ) : editingRecordId ? (
                    <div style={{
                      padding: '10px 16px',
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb',
                      backgroundColor: '#f9fafb',
                      fontSize: '14px',
                      fontWeight: '500',
                      color: '#374151',
                      direction: isRTL ? 'rtl' : 'ltr'
                    }}>
                      {(() => {
                        const studentId = parseInt(newQuranRecord.studentId);
                        const student = modalStudents.find(s => s.id === studentId) || 
                                       students.find(s => s.id === studentId);
                        return student ? (student.studentName || student.student_name || `${t('student')} ${student.id}`) : '';
                      })()}
                    </div>
                  ) : (
                    <select 
                      value={newQuranRecord.studentId}
                      onChange={async (e) => {
                        const selectedStudentId = e.target.value;
                        
                        if (selectedStudentId) {
                          const studentIdNum = parseInt(selectedStudentId);
                          
                          // ✅ Réinitialiser TOUS les champs du formulaire
                          setNewQuranRecord({
                            studentId: selectedStudentId,
                            totalHizb: '',
                            newHifzTxt: '',
                            oldHifzMurajahTxt: '',
                            newHifzMurajahText: '',
                            note: '',
                            nextHalaqaNewHifzText: '',
                            nextHalaqaOldHifzMurajahText: '',
                            nextHalaqaNewHifzMurajahText: '',
                            startPoint: 'الناس'
                          });
                          
                          // Réinitialiser les sélections de Coran
                          setNewMemorizationSelection({ startSurah: 0, startVerse: 0, endSurah: 0, endVerse: 0 });
                          setReviewSelection({ startSurah: 0, startVerse: 0, endSurah: 0, endVerse: 0 });
                          setNewHifzMurajahSelection({ startSurah: 0, startVerse: 0, endSurah: 0, endVerse: 0 });
                          setAssignmentSelection({ startSurah: 0, startVerse: 0, endSurah: 0, endVerse: 0 });
                          setNextHalaqaOldMurajahSelection({ startSurah: 0, startVerse: 0, endSurah: 0, endVerse: 0 });
                          setNextHalaqaNewMurajahSelection({ startSurah: 0, startVerse: 0, endSurah: 0, endVerse: 0 });
                          
                          // Réinitialiser les évaluations
                          setNewMemorizationEvaluation('لم يتم');
                          setReviewEvaluation('لم يتم');
                          setNewHifzMurajahEvaluation('لم يتم');
                          setEvalNewHifz('NotDone');
                          setEvalOldHifzMurajah('NotDone');
                          setEvalNewHifzMurajah('NotDone');
                          setRepeatNewHifz(0);
                          
                          // ✅ Mode ajout : copier les valeurs de "الجلسة القادمة" dans "آخر جلسة"
                          if (!editingRecordId && onGetNextSessionForStudent) {
                            try {
                              const nextSessionResponse = await onGetNextSessionForStudent(studentIdNum);
                              if (nextSessionResponse.success && nextSessionResponse.data) {
                                const nextSession = nextSessionResponse.data;
                                
                                // Parser une position
                                const parsePosition = (pos: string | QuranSelection | undefined): QuranSelection => {
                                  if (!pos) return { startSurah: 0, startVerse: 0, endSurah: 0, endVerse: 0 };
                                  if (typeof pos === 'object' && 'startSurah' in pos) {
                                    return pos as QuranSelection;
                                  }
                                  if (typeof pos === 'string' && pos.includes('-')) {
                                    const startEndPattern = /^(\d+):(\d+)-(\d+):(\d+)$/;
                                    const match = pos.match(startEndPattern);
                                    if (match) {
                                      return {
                                        startSurah: parseInt(match[1]),
                                        startVerse: parseInt(match[2]),
                                        endSurah: parseInt(match[3]),
                                        endVerse: parseInt(match[4])
                                      };
                                    }
                                  }
                                  return { startSurah: 0, startVerse: 0, endSurah: 0, endVerse: 0 };
                                };
                                
                                // ✅ Copier les valeurs de "الجلسة القادمة" dans "آخر جلسة"
                                // ET afficher les valeurs dans les champs "الجلسة القادمة"
                                setNewQuranRecord(prev => ({
                                  ...prev,
                                  // Copier les textes de "الجلسة القادمة" dans "آخر جلسة"
                                  newHifzTxt: nextSession.nextHalaqaNewHifzText || '',
                                  oldHifzMurajahTxt: nextSession.nextHalaqaOldHifzMurajahText || '',
                                  newHifzMurajahText: nextSession.nextHalaqaNewHifzMurajahText || '',
                                  // ✅ Afficher les valeurs dans les champs "الجلسة القادمة"
                                  nextHalaqaNewHifzText: nextSession.nextHalaqaNewHifzText || '',
                                  nextHalaqaOldHifzMurajahText: nextSession.nextHalaqaOldHifzMurajahText || '',
                                  nextHalaqaNewHifzMurajahText: nextSession.nextHalaqaNewHifzMurajahText || ''
                                }));
                                
                                // ✅ Copier les ranges (positions) pour "آخر جلسة"
                                if (nextSession.nextHalaqaNewHifzRange) {
                                  setNewMemorizationSelection(parsePosition(nextSession.nextHalaqaNewHifzRange));
                                }
                                if (nextSession.nextHalaqaOldHifzMurajahRange) {
                                  setReviewSelection(parsePosition(nextSession.nextHalaqaOldHifzMurajahRange));
                                }
                                if (nextSession.nextHalaqaNewHifzMurajahRange) {
                                  setNewHifzMurajahSelection(parsePosition(nextSession.nextHalaqaNewHifzMurajahRange));
                                }
                                
                                // ✅ Afficher les ranges (positions) pour "الجلسة القادمة"
                                if (nextSession.nextHalaqaNewHifzRange) {
                                  setAssignmentSelection(parsePosition(nextSession.nextHalaqaNewHifzRange));
                                }
                                if (nextSession.nextHalaqaOldHifzMurajahRange) {
                                  setNextHalaqaOldMurajahSelection(parsePosition(nextSession.nextHalaqaOldHifzMurajahRange));
                                }
                                if (nextSession.nextHalaqaNewHifzMurajahRange) {
                                  setNextHalaqaNewMurajahSelection(parsePosition(nextSession.nextHalaqaNewHifzMurajahRange));
                                }
                                
                                // ✅ Stocker repeatNewHifz de "الجلسة القادمة" pour la sauvegarder dans "آخر جلسة"
                                if (nextSession.repeatNewHifz != null) {
                                  setRepeatNewHifz(nextSession.repeatNewHifz);
                                }
                              }
                            } catch (error) {
                              console.warn('⚠️ Erreur lors de la récupération de الجلسة القادمة:', error);
                            }
                          } else if (editingRecordId) {
                            // Mode édition : charger les données du dernier enregistrement
                            
                            if (surahs.length === 0) {
                              await loadSurahs();
                            }
                            
                            const studentRecords = quranRecords.filter(r => {
                              const match = (r.studentId === studentIdNum) ||
                                           (r.studentName === students.find(s => s.id === studentIdNum)?.studentName);
                              return match;
                            });
                            
                            if (studentRecords.length > 0) {
                              const sortedRecords = studentRecords.sort((a, b) => {
                                const dateA = new Date(a.modifiedDate || a.createdDate || 0).getTime();
                                const dateB = new Date(b.modifiedDate || b.createdDate || 0).getTime();
                                return dateB - dateA;
                              });
                              
                              const latestRecord = sortedRecords[0];
                              
                              // Charger les données du dernier enregistrement dans le formulaire
                              loadRecordDataIntoForm(latestRecord);
                            }
                          }
                        }
                      }}
                      required={!editingRecordId}
                      disabled={editingRecordId !== null}
                      style={{...finalStyles.select, padding: '8px', fontSize: '13px'}}
                    >
                      <option value="">{t('selectStudent')}</option>
                      {modalStudents.length > 0 ? (
                        modalStudents.map(student => (
                          <option key={student.id} value={String(student.id)}>
                            {GLOBAL_UTILS.cleanText(student.studentName || student.student_name || `طالب ${student.id}`)}
                          </option>
                        ))
                      ) : editingRecordId ? (
                        // En mode édition, utiliser students comme fallback
                        students.length > 0 ? (
                          students.map(student => (
                            <option key={student.id} value={String(student.id)}>
                              {GLOBAL_UTILS.cleanText(student.studentName || student.student_name || `طالب ${student.id}`)}
                            </option>
                          ))
                        ) : (
                          <option value="" disabled>{t('noStudentsAvailable')}</option>
                        )
                      ) : (
                        // En mode ajout, ne pas utiliser students comme fallback (tous les étudiants ont déjà un record aujourd'hui)
                        <option value="" disabled>جميع الطلاب لديهم سجل اليوم</option>
                      )}
                    </select>
                  )}
                </div>

                {/* بداية الحفظ */}
                <div style={finalStyles.formGroup}>
                  <label style={{...finalStyles.label, fontSize: '14px', marginBottom: '6px'}}>بداية الحفظ</label>
                  <select 
                    value={newQuranRecord.startPoint}
                    onChange={(e) => setNewQuranRecord(prev => ({...prev, startPoint: e.target.value}))}
                    style={{...finalStyles.select, padding: '8px', fontSize: '13px'}}
                  >
                    <option value="الناس">الناس</option>
                    <option value="البقرة">البقرة</option>
                  </select>
                </div>

                {/* عدد الأحزاب */}
                <div style={finalStyles.formGroup}>
                  <label style={{...finalStyles.label, fontSize: '14px', marginBottom: '6px'}}>
                    الأحزاب
                    {calculatingHizb && (
                      <span style={{ fontSize: '11px', color: '#6b7280', marginRight: '4px', fontWeight: 'normal' }}>
                        (...)
                      </span>
                    )}
                  </label>
                  <input 
                    type="number" 
                    step="0.01"
                    value={newQuranRecord.totalHizb}
                    onChange={(e) => setNewQuranRecord(prev => ({...prev, totalHizb: e.target.value}))}
                    required 
                    readOnly={newMemorizationSelection.startSurah > 0 && newMemorizationSelection.endSurah > 0}
                    style={{
                      ...finalStyles.input,
                      padding: '8px',
                      fontSize: '13px',
                      backgroundColor: (newMemorizationSelection.startSurah > 0 && newMemorizationSelection.endSurah > 0) ? '#f3f4f6' : undefined,
                      cursor: (newMemorizationSelection.startSurah > 0 && newMemorizationSelection.endSurah > 0) ? 'not-allowed' : undefined
                    }}
                  />
                </div>
              </div>
              {/*newMemorizationSelection.startSurah > 0 && newMemorizationSelection.endSurah > 0 && (
                <p style={{ fontSize: '12px', color: '#059669', marginTop: '-12px', direction: 'rtl' }}>
                  ✓ يتم الحساب تلقائياً من الحفظ الجديد
                </p>
              )*/}

              {/* ========== ONGLETS: آخر جلسة / الجلسة القادمة ========== */}
              <div style={{ marginTop: '12px' }}>
                {/* Boutons d'onglets */}
                <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', direction: isRTL ? 'rtl' : 'ltr' }}>
                  <button
                    type="button"
                    onClick={() => setActiveTab('lastSession')}
                    style={{
                      flex: 1,
                      padding: '10px 16px',
                      fontSize: '14px',
                      fontWeight: '600',
                      border: activeTab === 'lastSession' ? '2px solid #10b981' : '2px solid #e5e7eb',
                      borderRadius: '8px',
                      backgroundColor: activeTab === 'lastSession' ? '#d1fae5' : '#f9fafb',
                      color: activeTab === 'lastSession' ? '#047857' : '#6b7280',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px'
                    }}
                  >
                    <span>📖</span>
                    <span>{qt.lastSession}</span>
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => setActiveTab('nextSession')}
                    style={{
                      flex: 1,
                      padding: '10px 16px',
                      fontSize: '14px',
                      fontWeight: '600',
                      border: activeTab === 'nextSession' ? '2px solid #f59e0b' : '2px solid #e5e7eb',
                      borderRadius: '8px',
                      backgroundColor: activeTab === 'nextSession' ? '#fef3c7' : '#f9fafb',
                      color: activeTab === 'nextSession' ? '#92400e' : '#6b7280',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px'
                    }}
                  >
                    <span>📚</span>
                    <span>{qt.nextSession}</span>
                  </button>
                </div>

                {/* Contenu de l'onglet actif */}
                {activeTab === 'lastSession' && (
                  <div style={{
                    backgroundColor: '#f0fdf4',
                    border: '2px solid #bbf7d0',
                    borderRadius: '8px',
                    padding: '12px'
                  }}>
                    {/* UNE SEULE COLONNE avec tous les champs */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {/* 1. الحفظ الجديد */}
                      {renderQuranSelection('الحفظ الجديد', newMemorizationSelection, setNewMemorizationSelection, 'newMemorization', true, newMemorizationEvaluation, handleNewMemorizationEvaluationChange)}
                      
                      {/* 2. مراجعة الحفظ القديم */}
                      {renderQuranSelection('مراجعة الحفظ القديم', reviewSelection, setReviewSelection, 'review', false, reviewEvaluation, handleReviewEvaluationChange)}
                      
                      {/* 3. مراجعة الحفظ القريب */}
                      {renderQuranSelection('مراجعة الحفظ القريب', newHifzMurajahSelection, setNewHifzMurajahSelection, 'review', false, newHifzMurajahEvaluation, handleNewHifzMurajahEvaluationChange)}
                      
                      {/* 4. الملاحظة - NOUVELLE POSITION */}
                      <div style={finalStyles.formGroup}>
                        <label style={{
                          ...finalStyles.label, 
                          fontSize: '13px', 
                          marginBottom: '4px',
                          fontWeight: '500'
                        }}>
                          الملاحظة
                        </label>
                        <textarea 
                          value={newQuranRecord.note}
                          onChange={handleNoteChange}
                          rows={1}
                          placeholder="ملاحظات الأستاذ..."
                          style={{
                            ...finalStyles.textarea, 
                            padding: '6px 8px', 
                            fontSize: '13px', 
                            resize: 'vertical',
                            minHeight: '32px'
                          }}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'nextSession' && (
                  <div style={{
                    backgroundColor: '#fef3c7',
                    border: '2px solid #fde047',
                    borderRadius: '8px',
                    padding: '12px'
                  }}>
                    {/* UNE SEULE COLONNE avec tous les champs */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {/* 1. الحفظ الجديد */}
                      {renderQuranSelection('الحفظ الجديد', assignmentSelection, setAssignmentSelection, 'assignment', false)}
                      
                      {/* 2. مراجعة الحفظ القديم */}
                      {renderQuranSelection('مراجعة الحفظ القديم', nextHalaqaOldMurajahSelection, setNextHalaqaOldMurajahSelection, 'assignment', false)}
                      
                      {/* 3. مراجعة الحفظ القريب */}
                      {renderQuranSelection('مراجعة الحفظ القريب', nextHalaqaNewMurajahSelection, setNextHalaqaNewMurajahSelection, 'assignment', false)}
                      
                      {/* 4. NOUVEAU: Badge تكرار الحفظ الجديد */}
                      {renderRepeatNewHifzBadge()}
                    </div>
                  </div>
                )}
              </div>

              {/* Boutons */}
              <div style={finalStyles.buttonGroup}>
                <button 
                  type="button"
                  onClick={() => {
                    setShowQuranRecordModal(false);
                    setEditingRecordId(null);
                    setNewQuranRecord({
                      studentId: '',
                      totalHizb: '',
                      newHifzTxt: '',
                      oldHifzMurajahTxt: '',
                      newHifzMurajahText: '',
                      nextHalaqaNewHifzText: '',
                      nextHalaqaOldHifzMurajahText: '',
                      nextHalaqaNewHifzMurajahText: '',
                      note: '',
                      startPoint: 'الناس'
                    });
                    setNewMemorizationSelection({ startSurah: 0, startVerse: 0, endSurah: 0, endVerse: 0 });
                    setReviewSelection({ startSurah: 0, startVerse: 0, endSurah: 0, endVerse: 0 });
                    setNewHifzMurajahSelection({ startSurah: 0, startVerse: 0, endSurah: 0, endVerse: 0 });
                    setAssignmentSelection({ startSurah: 0, startVerse: 0, endSurah: 0, endVerse: 0 });
                    setNextHalaqaOldMurajahSelection({ startSurah: 0, startVerse: 0, endSurah: 0, endVerse: 0 });
                    setNextHalaqaNewMurajahSelection({ startSurah: 0, startVerse: 0, endSurah: 0, endVerse: 0 });
                    // ✅ Réinitialiser repeatNewHifz
                    setRepeatNewHifz(0);
                  }}
                  style={{
                    ...finalStyles.button,
                    ...finalStyles.secondaryButton,
                    ...finalStyles.flexButton
                  }}
                >
                  {t('cancel')}
                </button>
                <button 
                  type="submit" 
                  style={{
                    ...finalStyles.button,
                    ...finalStyles.primaryButton,
                    ...finalStyles.flexButton
                  }}
                >
                  {editingRecordId ? t('update') : t('add')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuranTrackingSection;

